import { supabase } from "../../lib/supabase";
import { getUserByAuthId } from "./usersService";
import { getCardgames, saveActionLog } from "./systemService";
import { getFromCache, setToCache } from "./cacheService";
import { calculateFolderEstimatedValue, CardPriceItem, setPriceInCache, resolveGameSlug, convertUsdToBrl } from "../cardPriceService";
import {
  parseHomuraCard,
  parseHomuraCards,
  parseHomuraApiResponse,
  mapHomuraCardToCard,
  findMatchingVariant,
  cardBelongsToSet,
  extractCardSetInfo,
  cleanSetCodeString,
  UniversalCard,
  UniversalVariant
} from "../cardParser";
import {
  findGame,
  getGameSlug,
  getGameId,
  getGameName,
  getGameApiBaseUrl,
  isGame,
  isPokemonGame,
  isOnePieceGame
} from "../../constants/games";

export {
  parseHomuraCard,
  parseHomuraCards,
  parseHomuraApiResponse,
  mapHomuraCardToCard,
  findMatchingVariant,
  cardBelongsToSet,
  extractCardSetInfo,
  cleanSetCodeString
};
export type { UniversalCard, UniversalVariant };

/**
 * Helper to reliably extract tcgplayer id from any card representation
 */
export const extractCardTcgPlayerId = (card: any, variantNumberOrIndex: number = 0): string | null => {
  if (!card) return null;

  const isValidTcgId = (val: any): boolean => {
    if (val === undefined || val === null) return false;
    const str = String(val).trim();
    return str !== '' && str !== '0' && str !== 'null' && str !== 'undefined';
  };

  const getTcgIdFromObj = (obj: any): string | null => {
    if (!obj || typeof obj !== 'object') return null;
    const candidates = [
      obj.tcgplayer_id,
      obj.tcgplayerId,
      obj.tcgPlayerId,
      obj.tcgplayer?.productId,
      obj.tcgplayer?.id
    ];
    for (const c of candidates) {
      if (isValidTcgId(c)) return String(c).trim();
    }
    return null;
  };

  const variantsList: any[] = Array.isArray(card.variants) && card.variants.length > 0
    ? card.variants
    : (Array.isArray(card.raw?.variants) ? card.raw.variants : []);

  // 1. Se card.variant ou card.selectedVariant estiver explicitamente definido (uma variante específica foi escolhida)
  const explicitVariant = card.variant || card.selectedVariant;
  if (explicitVariant) {
    const fromVar = getTcgIdFromObj(explicitVariant);
    if (fromVar) return fromVar;
    // Se a variante específica foi fornecida e a carta possui variantes (ou variant_number > 1),
    // NUNCA deve pegar o tcgplayer_id de outra variante! Deve retornar null!
    if (variantsList.length > 0 || card.variant_number > 1 || card.variantNumber > 1) {
      return null;
    }
  }

  // 2. Se variantNumberOrIndex foi fornecido e a carta tem lista de variantes
  if (variantsList.length > 0) {
    const vNum = Number(
      variantNumberOrIndex > 0 
        ? variantNumberOrIndex 
        : (card.variant_number && Number(card.variant_number) > 0 ? card.variant_number : (card.variantNumber && Number(card.variantNumber) > 0 ? card.variantNumber : 1))
    );

    if (vNum > 0) {
      // Procura a variante correspondente pelo variant_number ou pelo índice
      const matchedVar = variantsList.find((v: any, idx: number) => {
        const itemNum = Number(v?.variant_number ?? v?.variantNumber ?? (idx + 1));
        return itemNum === vNum;
      }) || (vNum <= variantsList.length ? variantsList[vNum - 1] : null);

      if (matchedVar) {
        const fromMatched = getTcgIdFromObj(matchedVar);
        // Se a variante correspondente tem tcgplayer_id, retorna; senão, deve ficar NULL!
        // Não pode emprestar de v[0] ou de outra variante!
        return fromMatched;
      }
    }

    // Se há múltiplas variantes e card tem set_code, procura pela variante correspondente ao set_code
    const targetSetCode = String(card.set_code || card.setCode || '').trim().toLowerCase();
    if (targetSetCode && variantsList.length > 1) {
      const matchedBySet = variantsList.find((v: any) => {
        const vCode = String(v?.set_code || v?.setCode || (typeof v?.set === 'string' ? v.set : v?.set?.set_code || v?.set?.code) || '').trim().toLowerCase();
        return vCode === targetSetCode;
      });
      if (matchedBySet) {
        return getTcgIdFromObj(matchedBySet);
      }
    }

    // Se só tem 1 variante na lista
    if (variantsList.length === 1) {
      const fromSingle = getTcgIdFromObj(variantsList[0]);
      if (fromSingle) return fromSingle;
    } else {
      // Tem mais de 1 variante e não encontrou correspondência específica:
      // Se card.tcgplayer_id estiver explicitamente null, retorna null
      if (card.tcgplayer_id === null || card.tcgplayerId === null) {
        return null;
      }
    }
  }

  // 3. Fallback para carta simples (sem múltiplas variantes)
  const rootCandidates = [
    card.tcgplayer_id,
    card.tcgplayerId,
    card.tcgPlayerId,
    card.tcgplayer?.productId,
    card.tcgplayer?.id
  ];
  for (const c of rootCandidates) {
    if (isValidTcgId(c)) return String(c).trim();
  }

  return null;
};

/**
 * Helper to reliably extract external_id (which corresponds to the card ID in the database)
 */
export const extractCardExternalId = (card: any): string => {
  if (!card) return '';
  if (card.external_id && typeof card.external_id === 'string' && card.external_id.trim()) {
    return card.external_id.trim();
  }
  if (card.id !== undefined && card.id !== null) {
    const sId = String(card.id).trim();
    if (sId !== '' && sId !== 'null' && sId !== 'undefined') {
      return sId;
    }
  }
  if (card.code) {
    const sCode = String(card.code).trim();
    if (sCode !== '') return sCode;
  }
  if (card.number) {
    const sNum = String(card.number).trim();
    if (sNum !== '') return sNum;
  }
  if (card.pokemon?.number) {
    return String(card.pokemon.number).trim();
  }
  return '';
};

/**
 * Helper to get variants list from any card representation (variants, editions, result_editions)
 */
export const getCardRawVariants = (card: any): any[] => {
  if (!card) return [];
  if (Array.isArray(card.variants) && card.variants.length > 0) return card.variants;
  if (Array.isArray(card.editions) && card.editions.length > 0) return card.editions;
  if (Array.isArray(card.result_editions) && card.result_editions.length > 0) return card.result_editions;
  if (Array.isArray(card.raw?.variants) && card.raw.variants.length > 0) return card.raw.variants;
  return [];
};

/**
 * Helper to reliably extract variant_external_id from any card representation
 */
export const extractCardVariantExternalId = (card: any, variantNumber: number = 0): string | null => {
  if (!card) return null;
  const rawVariants = getCardRawVariants(card);
  const idx = variantNumber > 0 ? variantNumber - 1 : 0;
  const targetVar = card.variant || 
    card.selectedVariant || 
    (variantNumber > 0 && rawVariants.length > 0 ? (rawVariants.find((v: any) => Number(v?.variant_number ?? v?.variantNumber) === variantNumber) || rawVariants[idx] || rawVariants[variantNumber]) : null) ||
    (variantNumber === 0 && rawVariants.length > 0 ? rawVariants[0] : null) ||
    (rawVariants.length === 1 ? rawVariants[0] : null);

  const candidates = [
    card.variant?.variant_external_id,
    card.variant?.variantExternalId,
    card.selectedVariant?.variant_external_id,
    card.selectedVariant?.variantExternalId,
    card.variant_external_id,
    card.variantExternalId,
    targetVar?.variant_external_id,
    targetVar?.variantExternalId,
    targetVar?.uuid,
    targetVar?.unique_id,
    (targetVar?.id && isNaN(Number(targetVar.id))) ? String(targetVar.id) : null,
    (variantNumber > 0 && rawVariants.length > 0 && rawVariants[idx]?.uuid) ? String(rawVariants[idx].uuid) : null,
    (variantNumber > 0 && rawVariants.length > 0 && rawVariants[idx]?.id && isNaN(Number(rawVariants[idx].id))) ? String(rawVariants[idx].id) : null,
    (variantNumber > 0 && rawVariants.length > 0) ? rawVariants[idx]?.variant_external_id : null,
    (variantNumber > 0 && rawVariants.length > 0) ? rawVariants[idx]?.variantExternalId : null,
    (rawVariants.length === 1 && rawVariants[0]?.uuid) ? String(rawVariants[0].uuid) : null,
    (rawVariants.length === 1 && rawVariants[0]?.id && isNaN(Number(rawVariants[0].id))) ? String(rawVariants[0].id) : null,
    (rawVariants.length === 1) ? rawVariants[0]?.variant_external_id : null,
    (rawVariants.length === 1) ? rawVariants[0]?.variantExternalId : null
  ];

  for (const c of candidates) {
    if (c !== undefined && c !== null) {
      const str = String(c).trim();
      if (str !== '' && str !== 'null' && str !== 'undefined') {
        return str;
      }
    }
  }
  return null;
};

/**
 * Helper to reliably extract variant_code from any card representation
 */
export const extractCardVariantCode = (card: any, variantNumber: number = 0): string | null => {
  if (!card) return null;
  const rawVariants = getCardRawVariants(card);
  const idx = variantNumber > 0 ? variantNumber - 1 : 0;
  const targetVar = card.variant || 
    card.selectedVariant || 
    (variantNumber > 0 && rawVariants.length > 0 ? (rawVariants.find((v: any) => Number(v?.variant_number ?? v?.variantNumber) === variantNumber) || rawVariants[idx] || rawVariants[variantNumber]) : null) ||
    (variantNumber === 0 && rawVariants.length > 0 ? rawVariants[0] : null) ||
    (rawVariants.length === 1 ? rawVariants[0] : null);

  const candidates = [
    card.variant?.variant_code,
    card.variant?.variantCode,
    card.selectedVariant?.variant_code,
    card.selectedVariant?.variantCode,
    card.variant_code,
    card.variantCode,
    targetVar?.variant_code,
    targetVar?.variantCode,
    (variantNumber > 0 && rawVariants.length > 0) ? (rawVariants[idx]?.variant_code || rawVariants[idx]?.variantCode) : null,
    (rawVariants.length === 1) ? (rawVariants[0]?.variant_code || rawVariants[0]?.variantCode) : null,
    targetVar?.code,
    (variantNumber > 0 && rawVariants.length > 0) ? rawVariants[idx]?.code : null,
    (rawVariants.length === 1) ? rawVariants[0]?.code : null,
    // Se o targetVar?.set_code ou card.set_code tem hífen com número (ex: RDO-252, DOA Alter-081), na verdade é o variant_code!
    (targetVar?.set_code && String(targetVar.set_code).includes('-')) ? String(targetVar.set_code) : null,
    (card.set_code && String(card.set_code).includes('-')) ? String(card.set_code) : null,
    card.code,
    // Se o ID tiver hífen com número ou formato BT24-004_BT24 -> BT24-004
    (typeof card.id === 'string' && card.id.includes('_') && card.id.split('_')[0].includes('-')) ? card.id.split('_')[0] : null,
    (typeof card.external_id === 'string' && card.external_id.includes('_') && card.external_id.split('_')[0].includes('-')) ? card.external_id.split('_')[0] : null,
    (typeof card.id === 'string' && card.id.includes('-') && !card.id.includes('_')) ? card.id : null,
    (typeof card.external_id === 'string' && card.external_id.includes('-') && !card.external_id.includes('_')) ? card.external_id : null,
    targetVar?.collector_number,
    targetVar?.print_number,
    targetVar?.number,
    card.collector_number,
    card.print_number,
    card.card_no,
    card.card_num,
    card.number,
    (card.pokemon && card.pokemon.number) ? String(card.pokemon.number) : null,
  ];

  for (const c of candidates) {
    if (c !== undefined && c !== null) {
      const str = String(c).trim();
      if (str !== '' && str !== 'null' && str !== 'undefined') {
        return str;
      }
    }
  }
  return null;
};

/**
 * Helper to reliably extract set_code from any card representation
 */
export const extractCardSetCode = (card: any, variantNumber: number = 0): string | null => {
  if (!card) return null;
  const rawVariants = getCardRawVariants(card);
  const idx = variantNumber > 0 ? variantNumber - 1 : 0;
  const targetVar = card.variant || 
    card.selectedVariant || 
    (variantNumber > 0 && rawVariants.length > 0 ? (rawVariants.find((v: any) => Number(v?.variant_number ?? v?.variantNumber) === variantNumber) || rawVariants[idx] || rawVariants[variantNumber]) : null) ||
    (variantNumber === 0 && rawVariants.length > 0 ? rawVariants[0] : null) ||
    (rawVariants.length === 1 ? rawVariants[0] : null);

  const allVariantSetCodes = rawVariants.map((v: any) => 
    cleanSetCodeString(v?.set?.set_code) || 
    cleanSetCodeString(v?.set?.code) || 
    cleanSetCodeString(v?.set) || 
    cleanSetCodeString(v?.set_code) || 
    cleanSetCodeString(v?.setCode) || 
    cleanSetCodeString(v?.variant_code || v?.variantCode || v?.code)
  ).filter(Boolean);

  const candidates = [
    // 1. Explicit set object on variant or card
    cleanSetCodeString(targetVar?.set?.set_code),
    cleanSetCodeString(targetVar?.set?.code),
    cleanSetCodeString(card.set?.set_code),
    cleanSetCodeString(card.set?.code),
    cleanSetCodeString(targetVar?.set),
    cleanSetCodeString(card.set),
    // 2. set_code on variant or card (always cleaned to strip variant number suffix)
    cleanSetCodeString(targetVar?.set_code),
    cleanSetCodeString(targetVar?.setCode),
    cleanSetCodeString(card.set_code),
    cleanSetCodeString(card.setCode),
    // 3. Variant set codes
    allVariantSetCodes[0],
    // 4. Extracted from ID if format like 128167_TWM or BT24-004_BT24
    (typeof card.id === 'string' && card.id.includes('_')) ? cleanSetCodeString(card.id.split('_')[1]) : null,
    (typeof card.external_id === 'string' && card.external_id.includes('_')) ? cleanSetCodeString(card.external_id.split('_')[1]) : null,
    // 5. Code prefix (e.g. OP09 from OP09-087, FS02 from FS02-04, RDO from RDO-252)
    cleanSetCodeString(card.code),
    cleanSetCodeString(targetVar?.code),
    // 6. Set prefix property
    (card.set?.prefix) ? cleanSetCodeString(card.set.prefix) : null
  ];

  for (const c of candidates) {
    if (c) return c;
  }
  return null;
};

/**
 * Helper to reliably extract rarity from any card representation
 */
export const extractCardRarity = (card: any, variantNumber: number = 0): string | null => {
  if (!card) return null;
  const rawVariants = getCardRawVariants(card);
  const idx = variantNumber > 0 ? variantNumber - 1 : 0;
  const targetVar = card.variant || 
    card.selectedVariant || 
    (variantNumber > 0 && rawVariants.length > 0 ? (rawVariants.find((v: any) => Number(v?.variant_number ?? v?.variantNumber) === variantNumber) || rawVariants[idx] || rawVariants[variantNumber]) : null) ||
    (variantNumber === 0 && rawVariants.length > 0 ? rawVariants[0] : null) ||
    (rawVariants.length === 1 ? rawVariants[0] : null);

  const getRarityString = (val: any): string | null => {
    if (val === undefined || val === null) return null;
    if (typeof val === 'number') {
      const numMap: Record<number, string> = {
        1: 'Common',
        2: 'Uncommon',
        3: 'Rare',
        4: 'Super Rare',
        5: 'Ultra Rare',
        6: 'Secret Rare',
        7: 'Special Rare'
      };
      return numMap[val] || String(val);
    }
    if (typeof val === 'string') {
      const trimmed = val.trim();
      return (trimmed !== '' && trimmed !== 'null' && trimmed !== 'undefined') ? trimmed : null;
    }
    if (typeof val === 'object') {
      const str = val.name || val.label || val.title || val.rarity || val.rarity_code || val.code;
      if (str) return String(str).trim();
    }
    return null;
  };

  const allVariantRarities = rawVariants.map((v: any) => 
    getRarityString(v?.rarity) || 
    getRarityString(v?.set_rarity) || 
    getRarityString(v?.setRarity) || 
    getRarityString(v?.card_rarity) || 
    getRarityString(v?.rarity_code)
  ).filter(Boolean);

  const candidates = [
    getRarityString(targetVar?.rarity),
    getRarityString(targetVar?.set_rarity),
    getRarityString(targetVar?.setRarity),
    getRarityString(targetVar?.card_rarity),
    getRarityString(targetVar?.rarity_code),
    getRarityString(card.rarity),
    getRarityString(card.Rarity),
    getRarityString(card.set_rarity),
    getRarityString(card.setRarity),
    getRarityString(card.card_rarity),
    getRarityString(card.guardian?.rarity),
    allVariantRarities[0],
    getRarityString(card.raw_rarity),
    getRarityString(card.md_rarity)
  ];

  for (const c of candidates) {
    if (c !== undefined && c !== null) {
      const str = String(c).trim();
      if (str !== '' && str !== 'null' && str !== 'undefined') {
        return str;
      }
    }
  }
  return null;
};

export const syncCard = async (card: any) => {
  try {
    // Garante que o valor salvo em external_id no banco é o ID da carta da API
    let cardCode = '';
    if (typeof card.id === 'string' && card.id.trim()) {
      cardCode = card.id.trim();
    } else if (card.external_id && typeof card.external_id === 'string' && card.external_id.trim()) {
      cardCode = card.external_id.trim();
    } else if (card.id !== undefined && card.id !== null) {
      cardCode = String(card.id).trim();
    } else if (card.code) {
      cardCode = String(card.code).trim();
    } else if (card.number) {
      cardCode = String(card.number).trim();
    } else if (card.pokemon?.number) {
      cardCode = String(card.pokemon.number).trim();
    }

    const gameId = card.game_id || card.gameId || 1;

    if (!cardCode) {
      console.warn('syncCard: No card identifier found', card);
      return null;
    }

    const variantNum = Number(
      card.variant_number !== undefined && card.variant_number !== null
        ? card.variant_number
        : (card.variantNumber !== undefined && card.variantNumber !== null ? card.variantNumber : 1)
    );
    const validVariantNumber = !isNaN(variantNum) && variantNum > 0 ? variantNum : 1;

    // Resolve campos de variantes e metadados antecipadamente
    const imageToSave = card.imageUrl || card.image_url || card.image || 
      (card.variant ? (card.variant.image_url || card.variant.imageUrl || card.variant.image) : null) ||
      (typeof card.images === 'string' ? card.images : card.images?.small || card.images?.medium || card.images?.large) || null;

    const setCodeToSave = extractCardSetCode(card, validVariantNumber) || (card.set_code ? cleanSetCodeString(card.set_code) : null);
    const rarityToSave = extractCardRarity(card, validVariantNumber) || card.rarity || null;
    const variantExtIdVal = extractCardVariantExternalId(card, validVariantNumber) || card.variant_external_id || null;
    const variantCodeVal = extractCardVariantCode(card, validVariantNumber) || card.variant_code || null;
    const tcgplayerIdVal = extractCardTcgPlayerId(card, validVariantNumber);
    const isFoilVal = card.is_foil !== undefined 
      ? Boolean(card.is_foil) 
      : (card.isFoil !== undefined ? Boolean(card.isFoil) : (card.foil !== undefined ? Boolean(card.foil) : false));

    // 1. Resolve pricing
    let priceValUsd: number | null = null;
    if (card.variant) {
      const rawP = card.variant.price ?? card.variant.marketPrice ?? card.variant.set_price;
      if (rawP !== undefined && rawP !== null && !isNaN(Number(rawP)) && Number(rawP) > 0) {
        priceValUsd = Number(rawP);
      }
    }
    if (priceValUsd === null && typeof card.rawPrice === 'number' && card.rawPrice > 0) {
      priceValUsd = card.rawPrice;
    } else if (priceValUsd === null && typeof card.price === 'number' && card.price > 0) {
      // card.price pode ser o valor já em BRL (vindo da busca); converte para USD para gravar no banco
      priceValUsd = Math.round((card.price / 5) * 100) / 100;
    }
    const initialPrice = priceValUsd !== null ? Math.round(priceValUsd * 100) / 100 : null;
    const initialPriceUpdatedAt = initialPrice !== null ? new Date().toISOString() : null;

    // 2. Se card.card_db_id ou card.card_id for fornecido (ou card.id for numérico explícito de registro existente)
    const explicitDbId = card.card_db_id || card.card_id || (typeof card.id === 'number' && card.id > 0 ? card.id : null);
    if (explicitDbId) {
      const numId = Number(explicitDbId);
      if (!isNaN(numId) && numId > 0) {
        const { data: existingById } = await supabase
          .from('cards')
          .select('*')
          .eq('id', numId)
          .maybeSingle();

        const existingVar = Number(existingById?.variant_number || 1);
        if (existingById && (existingVar === validVariantNumber || (validVariantNumber === 1 && (existingById.variant_number === 0 || existingById.variant_number === null)))) {
          // Se campos chave estiverem ausentes ou set_code estiver incorreto (com hífen/variant_code), atualiza a tabela 'cards'
          const patchPayload: any = {};
          if ((!existingById.variant_code || existingById.variant_code !== variantCodeVal) && variantCodeVal) {
            patchPayload.variant_code = String(variantCodeVal);
            existingById.variant_code = patchPayload.variant_code;
          }
          if ((!existingById.variant_external_id || existingById.variant_external_id !== variantExtIdVal) && variantExtIdVal) {
            patchPayload.variant_external_id = String(variantExtIdVal);
            existingById.variant_external_id = patchPayload.variant_external_id;
          }
          if ((!existingById.rarity || existingById.rarity !== rarityToSave) && rarityToSave) {
            patchPayload.rarity = rarityToSave;
            existingById.rarity = patchPayload.rarity;
          }
          if (
            setCodeToSave &&
            (!existingById.set_code ||
              existingById.set_code === existingById.variant_code ||
              existingById.set_code === variantCodeVal ||
              (existingById.set_code !== setCodeToSave && (existingById.set_code.includes('-') || existingById.set_code.length > (setCodeToSave.length + 2))))
          ) {
            patchPayload.set_code = setCodeToSave;
            existingById.set_code = patchPayload.set_code;
          }
          if (Object.keys(patchPayload).length > 0) {
            await supabase.from('cards').update(patchPayload).eq('id', existingById.id);
          }

          if (initialPrice !== null) {
            setPriceInCache(resolveGameSlug(gameId), convertUsdToBrl(initialPrice), initialPriceUpdatedAt || undefined, String(cardCode), existingById.id);
          }
          return existingById;
        }
      }
    }

    // 3. Check existing record by (game_id, external_id, variant_number)
    let existingQuery = supabase
      .from('cards')
      .select('*')
      .eq('game_id', Number(gameId))
      .eq('external_id', String(cardCode));

    if (validVariantNumber > 1) {
      existingQuery = existingQuery.eq('variant_number', validVariantNumber);
    } else {
      existingQuery = existingQuery.or('variant_number.eq.1,variant_number.eq.0,variant_number.is.null');
    }

    const { data: existingByExtList } = await existingQuery.limit(1);
    let targetExisting = existingByExtList && existingByExtList.length > 0 ? existingByExtList[0] : null;

    // Se não encontrou por external_id exato (card.id), verifica se existe registro legado salvo com o code antigo
    if (!targetExisting && card.code && String(card.code) !== String(cardCode)) {
      let legacyQuery = supabase
        .from('cards')
        .select('*')
        .eq('game_id', Number(gameId))
        .eq('external_id', String(card.code));

      if (validVariantNumber > 1) {
        legacyQuery = legacyQuery.eq('variant_number', validVariantNumber);
      } else {
        legacyQuery = legacyQuery.or('variant_number.eq.1,variant_number.eq.0,variant_number.is.null');
      }

      const { data: legacyList } = await legacyQuery.limit(1);
      const legacyCard = legacyList && legacyList.length > 0 ? legacyList[0] : null;
      if (legacyCard) {
        targetExisting = legacyCard;
      }
    }

    if (targetExisting) {
      // Se campos chave estiverem ausentes ou set_code estiver incorreto (com hífen/variant_code), atualiza a tabela 'cards'
      const patchPayload: any = {};
      if ((!targetExisting.variant_code || targetExisting.variant_code !== variantCodeVal) && variantCodeVal) {
        patchPayload.variant_code = String(variantCodeVal);
        targetExisting.variant_code = patchPayload.variant_code;
      }
      if ((!targetExisting.variant_external_id || targetExisting.variant_external_id !== variantExtIdVal) && variantExtIdVal) {
        patchPayload.variant_external_id = String(variantExtIdVal);
        targetExisting.variant_external_id = patchPayload.variant_external_id;
      }
      if ((!targetExisting.rarity || targetExisting.rarity !== rarityToSave) && rarityToSave) {
        patchPayload.rarity = rarityToSave;
        targetExisting.rarity = patchPayload.rarity;
      }
      if (
        setCodeToSave &&
        (!targetExisting.set_code ||
          targetExisting.set_code === targetExisting.variant_code ||
          targetExisting.set_code === variantCodeVal ||
          (targetExisting.set_code !== setCodeToSave && (targetExisting.set_code.includes('-') || targetExisting.set_code.length > (setCodeToSave.length + 2))))
      ) {
        patchPayload.set_code = setCodeToSave;
        targetExisting.set_code = patchPayload.set_code;
      }
      if (Object.keys(patchPayload).length > 0) {
        await supabase.from('cards').update(patchPayload).eq('id', targetExisting.id);
      }

      if (initialPrice !== null) {
        setPriceInCache(resolveGameSlug(gameId), convertUsdToBrl(initialPrice), initialPriceUpdatedAt || undefined, String(cardCode), targetExisting.id);
      }
      return targetExisting;
    }

    // 4. Inserir novo registro na tabela 'cards' (compatível com a estrutura de constraints do banco)
    const insertPayload: any = {
      name: card.name || 'Unknown Card',
      external_id: String(cardCode),
      game_id: Number(gameId),
      variant_number: validVariantNumber,
      image_url: imageToSave,
      set_code: setCodeToSave,
      rarity: rarityToSave,
      is_foil: isFoilVal,
      variant_code: variantCodeVal ? String(variantCodeVal) : null,
      variant_external_id: variantExtIdVal ? String(variantExtIdVal) : null
    };
    if (initialPrice !== null) {
      insertPayload.price = initialPrice;
      insertPayload.price_updated_at = initialPriceUpdatedAt;
    }
    if (tcgplayerIdVal) {
      insertPayload.tcgplayer_id = String(tcgplayerIdVal);
    } else {
      insertPayload.tcgplayer_id = null;
    }

    const { data: synced, error: insertError } = await supabase
      .from('cards')
      .insert(insertPayload)
      .select()
      .maybeSingle();

    if (insertError) {
      console.warn('syncCard insert warning, trying fallback select:', insertError);

      let fallbackQuery = supabase
        .from('cards')
        .select('*')
        .eq('game_id', Number(gameId))
        .eq('external_id', String(cardCode));

      if (validVariantNumber > 1) {
        fallbackQuery = fallbackQuery.eq('variant_number', validVariantNumber);
      } else {
        fallbackQuery = fallbackQuery.or('variant_number.eq.1,variant_number.eq.0,variant_number.is.null');
      }

      const { data: fallbackList } = await fallbackQuery.limit(1);
      const existingFallback = fallbackList && fallbackList.length > 0 ? fallbackList[0] : null;

      if (existingFallback) {
        const patchFallback: any = {};
        if ((!existingFallback.variant_code || existingFallback.variant_code !== variantCodeVal) && variantCodeVal) {
          patchFallback.variant_code = String(variantCodeVal);
        }
        if ((!existingFallback.variant_external_id || existingFallback.variant_external_id !== variantExtIdVal) && variantExtIdVal) {
          patchFallback.variant_external_id = String(variantExtIdVal);
        }
        if ((!existingFallback.rarity || existingFallback.rarity !== rarityToSave) && rarityToSave) {
          patchFallback.rarity = rarityToSave;
        }
        if (
          setCodeToSave &&
          (!existingFallback.set_code ||
            existingFallback.set_code === existingFallback.variant_code ||
            existingFallback.set_code === variantCodeVal ||
            (existingFallback.set_code !== setCodeToSave && (existingFallback.set_code.includes('-') || existingFallback.set_code.length > (setCodeToSave.length + 2))))
        ) {
          patchFallback.set_code = setCodeToSave;
        }
        if (Object.keys(patchFallback).length > 0) {
          await supabase.from('cards').update(patchFallback).eq('id', existingFallback.id);
          Object.assign(existingFallback, patchFallback);
        }

        if (initialPrice !== null) {
          setPriceInCache(resolveGameSlug(gameId), convertUsdToBrl(initialPrice), initialPriceUpdatedAt || undefined, String(cardCode), existingFallback.id);
        }
        return existingFallback;
      }
      throw insertError;
    }

    if (initialPrice !== null && synced) {
      setPriceInCache(resolveGameSlug(gameId), convertUsdToBrl(initialPrice), initialPriceUpdatedAt || undefined, String(cardCode), synced.id);
    }
    return synced;
  } catch (error) {
    console.error('Error in syncCard:', error);
    throw error;
  }
};


export const addCardToList = async (userId: any, listType: 'cards' | 'wishlist' | 'offerlist', card: any, quantity: number = 1) => {
  const table = listType === 'cards' ? 'user_collection' : (listType === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  
  try {
    // Ensure card exists in our DB 'cards' table and get its valid DB primary key ID
    const dbCard = await syncCard(card);
    if (!dbCard || !dbCard.id) {
      throw new Error('Não foi possível sincronizar a carta no banco de dados.');
    }

    const dbCardId = dbCard.id;
    const dbGameId = dbCard.game_id || card.game_id || 1;

    // Check if card already exists in list for this user
    const { data: existing, error: fetchError } = await supabase
      .from(table)
      .select('*')
      .eq('user_id', userId)
      .eq('card_id', dbCardId)
      .maybeSingle();

    if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;

    if (existing) {
      const { error: updateError } = await supabase
        .from(table)
        .update({ quantity: (existing.quantity || 0) + quantity })
        .eq('id', existing.id);
      if (updateError) throw updateError;
    } else {
      const insertPayload: any = {
        user_id: userId,
        card_id: dbCardId,
        quantity: quantity,
        game_id: dbGameId
      };

      const { error: insertError } = await supabase
        .from(table)
        .insert(insertPayload);

      if (insertError) {
        if (insertError.code === '23505') {
          const { data: secondFetch } = await supabase
            .from(table)
            .select('*')
            .eq('user_id', userId)
            .eq('card_id', dbCardId)
            .maybeSingle();
          if (secondFetch) {
            await supabase
              .from(table)
              .update({ quantity: (secondFetch.quantity || 0) + quantity })
              .eq('id', secondFetch.id);
          }
        } else {
          throw insertError;
        }
      }
    }

    // Log action
    await saveActionLog(
      'add_card', 
      'user_collection', 
      String(dbCardId), 
      { 
        destino: listType, 
        name: card.name, 
        quantity: quantity,
        message: `adicionou "${card.name}" (${quantity}) à sua ${listType === 'cards' ? 'coleção' : (listType === 'wishlist' ? 'lista de desejos' : 'lista de trocas')}`
      }
    );

    invalidateFoldersStatsCache(userId);
  } catch (error: any) {
    console.error(`Error adding card to ${listType}:`, error);
    throw error;
  }
};

/**
 * Returns a set of all card identifiers (card_id, external_id, code) currently in the user's wishlist
 */
export const getUserWishlistCardIds = async (userId: string | number): Promise<Set<string>> => {
  if (!userId) return new Set();
  try {
    const { data, error } = await supabase
      .from('user_wishlist')
      .select('card_id, card:cards(id, external_id)')
      .eq('user_id', userId);

    if (error || !data) return new Set();

    const ids = new Set<string>();
    data.forEach((item: any) => {
      if (item.card_id) ids.add(String(item.card_id));
      const c = item.card;
      if (c?.id) ids.add(String(c.id));
      if (c?.external_id) ids.add(String(c.external_id));
      if (c?.code) ids.add(String(c.code).toLowerCase());
    });
    return ids;
  } catch (e) {
    console.error('Error fetching user wishlist card IDs:', e);
    return new Set();
  }
};

/**
 * Returns a set of all card identifiers (card_id, external_id, code) currently in the user's offerlist
 */
export const getUserOfferlistCardIds = async (userId: string | number): Promise<Set<string>> => {
  if (!userId) return new Set();
  try {
    const { data, error } = await supabase
      .from('user_offerlist')
      .select('card_id, card:cards(id, external_id)')
      .eq('user_id', userId);

    if (error || !data) return new Set();

    const ids = new Set<string>();
    data.forEach((item: any) => {
      if (item.card_id) ids.add(String(item.card_id));
      const c = item.card;
      if (c?.id) ids.add(String(c.id));
      if (c?.external_id) ids.add(String(c.external_id));
      if (c?.code) ids.add(String(c.code).toLowerCase());
    });
    return ids;
  } catch (e) {
    console.error('Error fetching user offerlist card IDs:', e);
    return new Set();
  }
};

/**
 * Toggles a card in the user's wishlist.
 * If already in wishlist, removes it. If not, adds it.
 */
export const toggleWishlistCard = async (
  userId: string | number,
  card: any
): Promise<{ added: boolean }> => {
  if (!userId) throw new Error('Usuário não autenticado.');

  const dbCard = await syncCard(card);
  if (!dbCard?.id) {
    throw new Error('Não foi possível identificar a carta no catálogo.');
  }

  const dbCardId = dbCard.id;
  const dbGameId = dbCard.game_id || card.game_id || 1;

  // Check if exists in user_wishlist
  const { data: existing, error: fetchErr } = await supabase
    .from('user_wishlist')
    .select('id')
    .eq('user_id', userId)
    .eq('card_id', dbCardId)
    .maybeSingle();

  if (fetchErr && fetchErr.code !== 'PGRST116') throw fetchErr;

  if (existing) {
    const { error: delErr } = await supabase
      .from('user_wishlist')
      .delete()
      .eq('id', existing.id);
    if (delErr) throw delErr;

    await saveActionLog(
      'remove_card',
      'user_wishlist',
      String(dbCardId),
      {
        destino: 'wishlist',
        name: card.name,
        message: `removeu "${card.name}" da lista de desejos`
      }
    );

    invalidateFoldersStatsCache(userId);
    return { added: false };
  } else {
    const { error: insErr } = await supabase
      .from('user_wishlist')
      .insert({
        user_id: userId,
        card_id: dbCardId,
        quantity: 1,
        game_id: dbGameId
      });
    if (insErr) throw insErr;

    await saveActionLog(
      'add_card',
      'user_wishlist',
      String(dbCardId),
      {
        destino: 'wishlist',
        name: card.name,
        quantity: 1,
        message: `adicionou "${card.name}" à lista de desejos`
      }
    );

    invalidateFoldersStatsCache(userId);
    return { added: true };
  }
};

export interface BatchAddResult {
  added: number;
  updated: number;
  total: number;
  skipped: number;
  errors: number;
}

/**
 * Adds multiple cards to user collection, wishlist or offerlist in batch.
 * Highly optimized with batch lookups, parallel sync and chunked inserts.
 */
export const addCardsBatchToList = async (
  userId: string | number,
  listType: 'cards' | 'wishlist' | 'offerlist',
  cards: any[],
  options?: {
    quantity?: number;
    useCardQuantity?: boolean;
    onlyMissing?: boolean;
    setCode?: string;
    onProgress?: (processed: number, total: number) => void;
  }
): Promise<BatchAddResult> => {
  if (!userId) throw new Error('Usuário não autenticado.');
  if (!cards || cards.length === 0) {
    return { added: 0, updated: 0, total: 0, skipped: 0, errors: 0 };
  }

  const table = listType === 'cards' ? 'user_collection' : (listType === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  const defaultQuantity = options?.quantity || 1;
  const onlyMissing = Boolean(options?.onlyMissing);
  const total = cards.length;

  let added = 0;
  let updated = 0;
  let skipped = 0;
  let errors = 0;

  // 1. First, check which cards already exist in 'cards' table to minimize individual syncCard calls
  const cardExtMap = new Map<string, any>();
  cards.forEach(c => {
    const extId = extractCardExternalId(c);
    if (extId) cardExtMap.set(extId, c);
    if (c.external_id && typeof c.external_id === 'string' && c.external_id.trim()) {
      cardExtMap.set(c.external_id.trim(), c);
    }
    if (c.id !== undefined && c.id !== null) {
      const sId = String(c.id).trim();
      if (sId && sId !== 'null' && sId !== 'undefined') cardExtMap.set(sId, c);
    }
    if (c.code && typeof c.code === 'string' && c.code.trim()) {
      cardExtMap.set(c.code.trim(), c);
    }
  });

  const extIds = Array.from(cardExtMap.keys());
  const allDbMatches: any[] = [];

  // Batch query cards table in chunks of 100
  for (let i = 0; i < extIds.length; i += 100) {
    const chunkIds = extIds.slice(i, i + 100);
    const { data: dbMatches, error } = await supabase
      .from('cards')
      .select('*')
      .in('external_id', chunkIds);

    if (!error && dbMatches) {
      allDbMatches.push(...dbMatches);
    }
  }

  // 2. For cards not found in 'cards' table, sync them in parallel chunks of 10
  const syncedCards: Array<{ dbCard: any; originalCard: any }> = [];
  const missingSyncCards: any[] = [];
  const existingCardUpdates: Promise<any>[] = [];

  cards.forEach(c => {
    const extId = extractCardExternalId(c);
    const rawVar = c.variant_number !== undefined && c.variant_number !== null ? c.variant_number : c.variantNumber;
    const targetVarNum = (rawVar !== undefined && rawVar !== null && Number(rawVar) > 0) ? Number(rawVar) : 1;
    const targetSetCode = String(c.set_code || c.setCode || options?.setCode || '').trim().toLowerCase();
    const cDbId = Number(c.card_db_id || c.card_id || (typeof c.id === 'number' && c.id > 0 ? c.id : null));

    // Find DB match prioritizing explicit ID, matching external_id AND variant_number (or set_code)
    let existingDb = (cDbId && !isNaN(cDbId))
      ? allDbMatches.find((dbc: any) => Number(dbc.id) === cDbId)
      : null;

    if (!existingDb) {
      existingDb = allDbMatches.find((dbc: any) => 
        (String(dbc.external_id).trim() === extId || String(dbc.external_id).trim() === String(c.external_id || '').trim() || String(dbc.external_id).trim() === String(c.id || '').trim()) && 
        (Number(dbc.variant_number || 1) === targetVarNum || (targetVarNum === 1 && (dbc.variant_number === 0 || dbc.variant_number === null)))
      );
    }

    if (!existingDb && targetSetCode) {
      existingDb = allDbMatches.find((dbc: any) => 
        (String(dbc.external_id).trim() === extId || String(dbc.external_id).trim() === String(c.external_id || '').trim() || String(dbc.external_id).trim() === String(c.id || '').trim()) && 
        String(dbc.set_code || '').trim().toLowerCase() === targetSetCode
      );
    }

    if (!existingDb && targetVarNum === 1) {
      existingDb = allDbMatches.find((dbc: any) => 
        String(dbc.external_id).trim() === extId || 
        String(dbc.external_id).trim() === String(c.external_id || '').trim() || 
        String(dbc.external_id).trim() === String(c.id || '').trim()
      );
    }

    if (existingDb && existingDb.id) {
      // Se a carta já existe mas está com campos nulos ou set_code incorreto (ex: gravado com variant_code), atualiza os campos na tabela 'cards'
      const targetVarCode = extractCardVariantCode(c, targetVarNum);
      const targetVarExtId = extractCardVariantExternalId(c, targetVarNum);
      const targetRarity = extractCardRarity(c, targetVarNum);
      const targetSetCodeFixed = extractCardSetCode(c, targetVarNum);

      const patch: any = {};
      if ((!existingDb.variant_code || existingDb.variant_code !== targetVarCode) && targetVarCode) {
        patch.variant_code = String(targetVarCode);
        existingDb.variant_code = patch.variant_code;
      }
      if ((!existingDb.variant_external_id || existingDb.variant_external_id !== targetVarExtId) && targetVarExtId) {
        patch.variant_external_id = String(targetVarExtId);
        existingDb.variant_external_id = patch.variant_external_id;
      }
      if ((!existingDb.rarity || existingDb.rarity !== targetRarity) && targetRarity) {
        patch.rarity = targetRarity;
        existingDb.rarity = patch.rarity;
      }
      if (
        targetSetCodeFixed &&
        (!existingDb.set_code ||
          existingDb.set_code === existingDb.variant_code ||
          existingDb.set_code === targetVarCode ||
          (existingDb.set_code !== targetSetCodeFixed && (existingDb.set_code.includes('-') || existingDb.set_code.length > (targetSetCodeFixed.length + 2))))
      ) {
        patch.set_code = targetSetCodeFixed;
        existingDb.set_code = patch.set_code;
      }

      if (Object.keys(patch).length > 0) {
        existingCardUpdates.push(
          Promise.resolve(supabase.from('cards').update(patch).eq('id', existingDb.id))
        );
      }

      syncedCards.push({ dbCard: existingDb, originalCard: c });
    } else {
      missingSyncCards.push(c);
    }
  });

  if (existingCardUpdates.length > 0) {
    await Promise.allSettled(existingCardUpdates);
  }

  if (missingSyncCards.length > 0) {
    const SYNC_CHUNK_SIZE = 10;
    for (let i = 0; i < missingSyncCards.length; i += SYNC_CHUNK_SIZE) {
      const chunk = missingSyncCards.slice(i, i + SYNC_CHUNK_SIZE);
      const chunkPromises = chunk.map(async (c) => {
        try {
          const rawVar = c.variant_number !== undefined && c.variant_number !== null ? c.variant_number : c.variantNumber;
          const varNum = (rawVar !== undefined && rawVar !== null && Number(rawVar) > 0) ? Number(rawVar) : 1;
          const targetVarCode = extractCardVariantCode(c, varNum);
          const targetVarExtId = extractCardVariantExternalId(c, varNum);
          const targetRarity = extractCardRarity(c, varNum);
          const targetSetCode = extractCardSetCode(c, varNum);

          const cardToSync = {
            ...c,
            variant_number: varNum,
            set_code: targetSetCode || (c.set_code ? cleanSetCodeString(c.set_code) : null),
            rarity: targetRarity || c.rarity || null,
            variant_code: targetVarCode || c.variant_code || null,
            variant_external_id: targetVarExtId || c.variant_external_id || null
          };
          const dbCard = await syncCard(cardToSync);
          return { dbCard, originalCard: c };
        } catch (err) {
          console.warn('Error syncing card in batch:', c?.name, err);
          return { dbCard: null, originalCard: c };
        }
      });

      const chunkResults = await Promise.allSettled(chunkPromises);
      chunkResults.forEach((res) => {
        if (res.status === 'fulfilled' && res.value?.dbCard?.id) {
          syncedCards.push(res.value);
        } else {
          errors++;
        }
      });

      if (options?.onProgress) {
        options.onProgress(
          Math.min(syncedCards.length, total),
          total
        );
      }
    }
  } else if (options?.onProgress) {
    options.onProgress(Math.floor(total * 0.5), total);
  }

  if (syncedCards.length === 0) {
    return { added, updated, total, skipped, errors };
  }

  // 3. Look up which of these cards already exist in user's target table (user_collection, user_wishlist, user_offerlist)
  const dbCardIds = Array.from(new Set(syncedCards.map(sc => Number(sc.dbCard.id)).filter(id => !isNaN(id))));
  const existingUserMap = new Map<number, any>();

  for (let i = 0; i < dbCardIds.length; i += 100) {
    const idsChunk = dbCardIds.slice(i, i + 100);
    const { data: existingRows, error: fetchErr } = await supabase
      .from(table)
      .select('id, card_id, quantity')
      .eq('user_id', userId)
      .in('card_id', idsChunk);

    if (!fetchErr && existingRows) {
      existingRows.forEach((r: any) => {
        existingUserMap.set(Number(r.card_id), r);
      });
    }
  }

  // 4. Group into inserts and updates, avoiding duplicates within the batch
  const toInsert: any[] = [];
  const toUpdate: Array<{ id: any; quantity: number }> = [];
  const processedCardIds = new Set<number>();

  for (const { dbCard, originalCard } of syncedCards) {
    const cardId = Number(dbCard.id);
    if (processedCardIds.has(cardId)) {
      continue;
    }
    processedCardIds.add(cardId);

    const existingRow = existingUserMap.get(cardId);
    const gameId = dbCard.game_id || originalCard.game_id || 1;
    const cardQty = (options?.useCardQuantity && Number(originalCard?.quantity) > 0)
      ? Number(originalCard.quantity)
      : defaultQuantity;

    if (existingRow) {
      if (listType === 'wishlist' || onlyMissing) {
        skipped++;
      } else {
        toUpdate.push({
          id: existingRow.id,
          quantity: (existingRow.quantity || 0) + cardQty
        });
      }
    } else {
      toInsert.push({
        user_id: userId,
        card_id: cardId,
        quantity: cardQty,
        game_id: gameId
      });
    }
  }

  // 5. Batch insert in chunks of 50
  for (let i = 0; i < toInsert.length; i += 50) {
    const insertChunk = toInsert.slice(i, i + 50);
    const { error: insErr } = await supabase.from(table).insert(insertChunk);
    if (insErr) {
      console.warn('Batch insert error, falling back to individual inserts:', insErr);
      for (const row of insertChunk) {
        const { error: singleErr } = await supabase.from(table).insert(row);
        if (singleErr) errors++;
        else added++;
      }
    } else {
      added += insertChunk.length;
    }

    if (options?.onProgress) {
      options.onProgress(
        Math.min(total, (total * 0.7) + (added / Math.max(1, toInsert.length)) * (total * 0.3)),
        total
      );
    }
  }

  // 6. Update existing records
  if (toUpdate.length > 0) {
    for (const item of toUpdate) {
      const { error: updErr } = await supabase
        .from(table)
        .update({ quantity: item.quantity })
        .eq('id', item.id);
      if (updErr) errors++;
      else updated++;
    }
  }

  if (options?.onProgress) {
    options.onProgress(total, total);
  }

  // 7. Log action and invalidate cache
  if (added > 0 || updated > 0) {
    const listLabel = listType === 'cards' ? 'coleção' : (listType === 'wishlist' ? 'lista de desejos' : 'lista de ofertas (trocas)');
    await saveActionLog(
      'batch_add_cards',
      table,
      String(userId),
      {
        destino: listType,
        total,
        added,
        updated,
        skipped,
        message: `adicionou ${added} carta(s) em lote à sua ${listLabel}`
      }
    );
    invalidateFoldersStatsCache(userId);
  }

  return { added, updated, total, skipped, errors };
};

/**
 * Removes multiple cards from user's wishlist in batch.
 */
export const removeCardsBatchFromWishlist = async (
  userId: string | number,
  cards: any[]
): Promise<number> => {
  if (!userId || !cards || cards.length === 0) return 0;
  try {
    const cardDbIds: number[] = [];
    const extIds: string[] = [];

    cards.forEach(c => {
      if (c.card_db_id || c.card_id) {
        cardDbIds.push(Number(c.card_db_id || c.card_id));
      } else if (typeof c.id === 'number') {
        cardDbIds.push(c.id);
      }
      const ext = String(c.id || c.external_id || c.code || '').trim();
      if (ext) extIds.push(ext);
    });

    if (extIds.length > 0) {
      const { data: dbCards } = await supabase
        .from('cards')
        .select('id')
        .in('external_id', extIds);
      if (dbCards) {
        dbCards.forEach((dc: any) => {
          if (dc.id) cardDbIds.push(Number(dc.id));
        });
      }
    }

    const uniqueDbIds = Array.from(new Set(cardDbIds.filter(id => id && !isNaN(id))));
    if (uniqueDbIds.length === 0) return 0;

    let removed = 0;
    for (let i = 0; i < uniqueDbIds.length; i += 100) {
      const chunk = uniqueDbIds.slice(i, i + 100);
      const { error, count } = await supabase
        .from('user_wishlist')
        .delete({ count: 'exact' })
        .eq('user_id', userId)
        .in('card_id', chunk);

      if (!error && count) {
        removed += count;
      }
    }

    if (removed > 0) {
      invalidateFoldersStatsCache(userId);
    }
    return removed;
  } catch (err) {
    console.error('Error removing cards batch from wishlist:', err);
    return 0;
  }
};

/**
 * Removes multiple cards from user's offerlist in batch.
 */
export const removeCardsBatchFromOfferlist = async (
  userId: string | number,
  cards: any[]
): Promise<number> => {
  if (!userId || !cards || cards.length === 0) return 0;
  try {
    const cardDbIds: number[] = [];
    const extIds: string[] = [];

    cards.forEach(c => {
      if (c.card_db_id || c.card_id) {
        cardDbIds.push(Number(c.card_db_id || c.card_id));
      } else if (typeof c.id === 'number') {
        cardDbIds.push(c.id);
      }
      const ext = String(c.id || c.external_id || c.code || '').trim();
      if (ext) extIds.push(ext);
    });

    if (extIds.length > 0) {
      const { data: dbCards } = await supabase
        .from('cards')
        .select('id')
        .in('external_id', extIds);
      if (dbCards) {
        dbCards.forEach((dc: any) => {
          if (dc.id) cardDbIds.push(Number(dc.id));
        });
      }
    }

    const uniqueDbIds = Array.from(new Set(cardDbIds.filter(id => id && !isNaN(id))));
    if (uniqueDbIds.length === 0) return 0;

    let removed = 0;
    for (let i = 0; i < uniqueDbIds.length; i += 100) {
      const chunk = uniqueDbIds.slice(i, i + 100);
      const { error, count } = await supabase
        .from('user_offerlist')
        .delete({ count: 'exact' })
        .eq('user_id', userId)
        .in('card_id', chunk);

      if (!error && count) {
        removed += count;
      }
    }

    if (removed > 0) {
      invalidateFoldersStatsCache(userId);
    }
    return removed;
  } catch (err) {
    console.error('Error removing cards batch from offerlist:', err);
    return 0;
  }
};


export const getListCards = (userId: string | number, listType: 'cards' | 'wishlist' | 'offerlist', callback: (cards: any[]) => void) => {
  const table = listType === 'cards' ? 'user_collection' : (listType === 'wishlist' ? 'user_wishlist' : 'user_offerlist');

  const fetchList = async () => {
    let selectStr = '';
    if (listType === 'cards') selectStr = '*, card:cards(*, cardgame:games(name))';
    else if (listType === 'wishlist') selectStr = '*, card:cards(*, cardgame:games(name))';
    else selectStr = '*, card:cards(*, cardgame:games(name))';

    const { data, error } = await supabase
      .from(table)
      .select(selectStr)
      .eq('user_id', userId);
    
    if (error) {
      console.error(`Error fetching ${listType}:`, error);
      return;
    }

    // Flatten card data for backwards compatibility in UI
    const formattedData = ((data as any[]) || []).map(item => {
      const cardInfo = item.card;
      return {
        ...item,
        card_id: cardInfo?.id || item.card_id,
        card_db_id: cardInfo?.id || item.card_id,
        name: cardInfo?.name,
        image_url: cardInfo?.image_url,
        game: cardInfo?.cardgame?.name || 'Unknown',
        raridade: item.raridade || 'Common',
        external_id: cardInfo?.external_id,
        code: cardInfo?.external_id,
        game_id: item.game_id || cardInfo?.game_id,
        price: cardInfo?.price !== null && cardInfo?.price !== undefined ? convertUsdToBrl(Number(cardInfo?.price)) : null,
        price_updated_at: cardInfo?.price_updated_at || null,
        variant_number: (cardInfo?.variant_number && Number(cardInfo.variant_number) > 0) ? Number(cardInfo.variant_number) : 1,
        set_code: cardInfo?.set_code || null,
        rarity: cardInfo?.rarity || null,
        is_foil: cardInfo?.is_foil || false,
        variant_external_id: cardInfo?.variant_external_id || null,
        variant_code: cardInfo?.variant_code || null,
        tcgplayer_id: cardInfo?.tcgplayer_id || null,
        tcgplayerId: cardInfo?.tcgplayer_id || null
      };
    });

    callback(formattedData);
  };

  fetchList();

  const channelName = `public:${table}:${userId}:${Math.random().toString(36).substring(2)}`;
  const subscription = supabase
    .channel(channelName)
    .on('postgres_changes', { 
      event: '*', 
      schema: 'public', 
      table: table,
      filter: `user_id=eq.${userId}`
    }, fetchList)
    .subscribe();

  return () => {
    supabase.removeChannel(subscription);
  };
};


export const createBinder = async (userId: string | number, name: string, gameId: string, gameName: string) => {
  try {
    let internalId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.length > 30)) {
      const u = await getUserByAuthId(userId);
      if (u) internalId = u.id;
    }

    const { data, error } = await supabase
      .from('user_binders')
      .insert({
        user_id: internalId,
        name,
        game_id: gameId
      })
      .select()
      .maybeSingle();

    if (error) {
      // Fallback with original userId if internalId failed
      const { data: fbData, error: fbError } = await supabase
        .from('user_binders')
        .insert({
          user_id: userId,
          name,
          game_id: gameId
        })
        .select()
        .maybeSingle();
      if (fbError) throw fbError;
      return fbData?.id;
    }
    return data?.id;
  } catch (error) {
    console.error('Error creating binder:', error);
    throw error;
  }
};


export const getBinders = (userId: string | number, callback: (binders: any[]) => void) => {
    const fetchBinders = async () => {
    // If userId is UUID (from auth), we need the internal ID
    let internalId = userId;
    if (typeof userId === 'string' && userId.length > 30) {
      const { data: user } = await supabase.from('users').select('id').eq('auth_id', userId).maybeSingle();
      if (user) internalId = user.id;
    }

    const { data, error } = await supabase
      .from('user_binders')
      .select('*')
      .eq('user_id', internalId);
    
    if (error) {
      console.error('Error fetching binders:', error);
      return;
    }
    callback(data || []);
  };

  fetchBinders();

  const channelName = `public:user_binders:${userId}:${Math.random().toString(36).substring(2)}`;
  const subscription = supabase
    .channel(channelName)
    .on('postgres_changes', { 
      event: '*', 
      schema: 'public', 
      table: 'user_binders',
      filter: `user_id=eq.${typeof userId === 'string' && userId.length > 30 ? '0' : userId}` // This filter might not work with UUID if the column is bigint
    }, fetchBinders)
    .subscribe();

  return () => {
    supabase.removeChannel(subscription);
  };
};


export const addCardToBinder = async (userId: any, binderId: any, card: any) => {
  try {
    // 1. Ensure we have the base card ID (BIGINT) from 'cards' table
    const synced = await syncCard(card);
    if (!synced || !synced.id) {
      throw new Error('Falha ao sincronizar carta com o servidor.');
    }
    const dbCardId = Number(synced.id);
    const parsedBinderId = Number(binderId);

    // 2. Add the link in binder_cards using (binder_id, card_id)
    const { data: inserted, error: insertError } = await supabase
      .from('binder_cards')
      .upsert({
        binder_id: parsedBinderId,
        card_id: dbCardId
      }, { onConflict: 'binder_id,card_id' })
      .select('id')
      .maybeSingle();
    
    if (insertError) {
      if (insertError.code === '23505') {
        console.info('Card already in binder:', dbCardId);
        return { success: true, alreadyExists: true };
      }
      throw insertError;
    }

    // Pastas personalizadas (binder_cards) não afetam a pasta do sistema coleção (user_collection)

    // Log addition to binder
    await saveActionLog(
      'add_to_folder', 
      'user_binders', 
      String(binderId), 
      { 
        card_name: card.name, 
        message: `organizou "${card.name}" em uma de suas pastas de coleção`
      }
    );

    invalidateFoldersStatsCache(userId);
    return { success: true, id: inserted?.id };
  } catch (error: any) {
    console.error('Error adding card to binder:', error);
    throw error;
  }
};


export const updateCardQuantityInList = async (userId: string | number, listType: 'cards' | 'wishlist' | 'offerlist', cardId: string, quantity: number) => {
  const table = listType === 'cards' ? 'user_collection' : (listType === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  try {
    const { error } = await supabase
      .from(table)
      .update({ quantity: quantity })
      .eq('user_id', userId)
      .eq('card_id', cardId);
    
    if (error) throw error;
    invalidateFoldersStatsCache(userId);
  } catch (error) {
    console.error(`Error updating card quantity in ${listType}:`, error);
    throw error;
  }
};


export const updateCardQuantityInBinder = async (dbId: any, quantity: number) => {
  try {
    if (quantity <= 0) {
      const num = Number(dbId);
      let query = supabase.from('binder_cards').delete();
      if (!isNaN(num)) {
        query = query.or(`id.eq.${num},card_id.eq.${num}`);
      } else {
        query = query.eq('id', dbId);
      }
      await query;
      invalidateFoldersStatsCache();
      return;
    }

    // Em pastas personalizadas (binder_cards), cada entrada é única (sem coluna quantity).
    // Não altera user_collection.
    invalidateFoldersStatsCache();
  } catch (error) {
    console.error('Error updating card quantity in binder:', error);
    throw error;
  }
};


export const removeCardFromList = async (userId: string | number, listType: 'cards' | 'wishlist' | 'offerlist', cardId: string) => {
  const table = listType === 'cards' ? 'user_collection' : (listType === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  try {
    const { error } = await supabase
      .from(table)
      .delete()
      .eq('user_id', userId)
      .eq('card_id', cardId);
    
    if (error) throw error;
    invalidateFoldersStatsCache(userId);
  } catch (error) {
    console.error(`Error removing card from ${listType}:`, error);
    throw error;
  }
};


export const moveCardBetweenLists = async (userId: string | number, fromList: 'cards' | 'wishlist' | 'offerlist', toList: 'cards' | 'wishlist' | 'offerlist', cardId: string) => {
  const fromTable = fromList === 'cards' ? 'user_collection' : (fromList === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  const toTable = toList === 'cards' ? 'user_collection' : (toList === 'wishlist' ? 'user_wishlist' : 'user_offerlist');
  
  try {
    // 1. Fetch card data from source list
    const { data: cardData, error: fetchError } = await supabase
      .from(fromTable)
      .select('*')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .maybeSingle();
    
    if (fetchError) throw fetchError;
    if (!cardData) throw new Error('Card not found in source list');

    // 2. Insert into destination list (using upsert logic similar to addCardToList)
    const { data: existing, error: checkError } = await supabase
      .from(toTable)
      .select('*')
      .eq('user_id', userId)
      .eq('card_id', cardId)
      .maybeSingle();
    
    if (checkError && checkError.code !== 'PGRST116') throw checkError;

    if (existing) {
      await supabase
        .from(toTable)
        .update({ quantity: existing.quantity + cardData.quantity })
        .eq('id', existing.id);
    } else {
      await supabase
        .from(toTable)
        .insert({
          user_id: userId,
          card_id: cardId,
          quantity: cardData.quantity
        });
    }

    // 3. Delete from source list
    await supabase
      .from(fromTable)
      .delete()
      .eq('id', cardData.id);

    invalidateFoldersStatsCache(userId);
  } catch (error) {
    console.error(`Error moving card from ${fromList} to ${toList}:`, error);
    throw error;
  }
};


export const deleteBinder = async (userId: string | number, binderId: string | number) => {
  try {
    let resolvedUserId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.length > 30)) {
      const u = await getUserByAuthId(userId);
      if (u) resolvedUserId = u.id;
    }

    // 1. Delete associations in binder_cards first
    const { error: relError } = await supabase
      .from('binder_cards')
      .delete()
      .eq('binder_id', binderId);
    
    if (relError) {
      console.warn('Notice while deleting binder_cards:', relError);
    }

    // 2. Delete the binder itself
    let { error: binderError } = await supabase
      .from('user_binders')
      .delete()
      .eq('id', binderId)
      .eq('user_id', resolvedUserId);
    
    if (binderError) {
      const res2 = await supabase
        .from('user_binders')
        .delete()
        .eq('id', binderId)
        .eq('user_id', userId);
      binderError = res2.error;
    }

    if (binderError) {
      const res3 = await supabase
        .from('user_binders')
        .delete()
        .eq('id', binderId);
      if (res3.error) throw res3.error;
    }

    invalidateFoldersStatsCache(resolvedUserId);
  } catch (error) {
    console.error('Error deleting binder:', error);
    throw error;
  }
};


export const getBinderWithCards = async (userId: string | number, binderId: string | number) => {
  try {
    let resolvedUserId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.length > 30)) {
      const u = await getUserByAuthId(userId);
      if (u) resolvedUserId = u.id;
    }

    // Fetch binder info first
    let { data: binder, error: binderError } = await supabase
      .from('user_binders')
      .select(`
        id, 
        name, 
        game_id,
        user_id,
        games!user_binders_game_id_fkey (name)
      `)
      .eq('id', binderId)
      .maybeSingle();
    
    if (binderError) throw binderError;
    if (!binder) return null;

    // Verify ownership or access
    const isOwner = 
      String(binder.user_id) === String(userId) || 
      String(binder.user_id) === String(resolvedUserId);

    // Fetch cards linked to this binder from the new binder_cards table (direct card_id relationship)
    const { data: cards, error: cardsError } = await supabase
      .from('binder_cards')
      .select(`
        id,
        binder_id,
        card_id,
        added_at,
        card:cards (
          *,
          cardgame:games (name)
        )
      `)
      .eq('binder_id', binderId)
      .order('added_at', { ascending: false });
    
    if (cardsError) throw cardsError;

    const gameName = Array.isArray(binder.games) 
      ? (binder.games as any)[0]?.name 
      : (binder.games as any)?.name;

    const formattedCards = (cards || []).map((c: any) => {
      const info = Array.isArray(c.card) ? c.card[0] : c.card;
      return {
        id: info?.id || c.card_id,
        dbId: c.id, // ID na tabela binder_cards
        binder_card_id: c.id,
        card_id: info?.id || c.card_id,
        card_db_id: info?.id || c.card_id,
        name: info?.beauty_name || info?.name,
        image_url: info?.image_url,
        game: info?.cardgame?.name || 'Unknown',
        raridade: info?.rarity || 'Common',
        external_id: info?.external_id,
        code: info?.external_id,
        game_id: info?.game_id || (binder as any)?.game_id,
        quantity: 1,
        price: info?.price !== null && info?.price !== undefined ? convertUsdToBrl(Number(info?.price)) : null,
        price_updated_at: info?.price_updated_at || null,
        variant_number: (info?.variant_number && Number(info.variant_number) > 0) ? Number(info.variant_number) : 1,
        set_code: info?.set_code || null,
        rarity: info?.rarity || null,
        is_foil: info?.is_foil || false,
        variant_external_id: info?.variant_external_id || null,
        variant_code: info?.variant_code || null,
        tcgplayer_id: info?.tcgplayer_id || null,
        tcgplayerId: info?.tcgplayer_id || null,
        added_at: c.added_at
      };
    });

    const estimatedValue = formattedCards.reduce((acc: number, c: any) => acc + ((c.price || 0) * (typeof c.quantity === 'number' ? c.quantity : 1)), 0);

    return {
      ...binder,
      game_name: gameName,
      cards: formattedCards,
      estimated_value: Math.round(estimatedValue * 100) / 100
    };
  } catch (error) {
    console.error('Error fetching binder with cards:', error);
    return null;
  }
};


export const removeCardFromBinder = async (binderId: string | number, cardIdentifier: string | number) => {
  try {
    const num = Number(cardIdentifier);
    let query = supabase
      .from('binder_cards')
      .delete()
      .eq('binder_id', binderId);

    if (!isNaN(num)) {
      query = query.or(`id.eq.${num},card_id.eq.${num}`);
    } else {
      query = query.eq('card_id', cardIdentifier);
    }
    
    const { error } = await query;
    if (error) throw error;
    invalidateFoldersStatsCache();
  } catch (error) {
    console.error('Error removing card from binder:', error);
    throw error;
  }
};


export const getCards = async (game?: string): Promise<any[]> => {
  try {
    let query = supabase.from('products').select('*, games(name)').limit(100);
    
    if (game && game !== 'All') {
      const { data: g } = await supabase.from('games').select('id').eq('name', game).maybeSingle();
      if (g) query = query.eq('game_id', g.id);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    
    return (data || []).map((p: any) => {
      const isTicket = p.product_type_id === 52 || p.product_type_id === 3 || p.product_types?.name === 'Ticket' || p.product_types?.name === 'Ingresso' || p.product_types?.name === 'Inscrição';
      const factor = isTicket ? 1 : 5;
      const originalMsrp = p.msrp || p.mspr || 0;
      return {
        id: p.id.toString(),
        name: p.beauty_name || p.name,
        game: p.games?.name || 'Unknown',
        code: p.slug || p.id.toString(),
        rarity: p.product_type || 'Common',
        price: Math.round(Number(originalMsrp) * factor * 100) / 100,
        imageUrl: p.image_url || 'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&q=80&w=400',
        set: p.games?.name || 'Base Set'
      };
    });
  } catch (error) {
    console.error('Error fetching cards:', error);
    return [];
  }
};


const parseCardSet = (setVal: any): string => {
  if (!setVal) return '';
  if (typeof setVal === 'string') return setVal;
  if (typeof setVal === 'object') {
    return setVal.set_name || setVal.name || setVal.beauty_name || setVal.set_code || setVal.code || setVal.title || '';
  }
  return String(setVal);
};

const parseCardText = (val: any): string => {
  if (!val) return '';
  if (typeof val === 'string') return val.replace(/<br\s*\/?>/gi, '\n').trim();
  if (typeof val === 'object') {
    const parts: string[] = [];
    if (val.card) {
      const c = parseCardText(val.card);
      if (c) parts.push(c);
    }
    if (val.rule) {
      const r = parseCardText(val.rule);
      if (r) parts.push(r);
    }
    if (val.ability) {
      const a = parseCardText(val.ability);
      if (a) parts.push(a);
    }
    if (val.effect) {
      const e = parseCardText(val.effect);
      if (e) parts.push(e);
    }

    const sortedKeys = Object.keys(val).sort();
    sortedKeys.forEach(k => {
      if (
        (k.startsWith('attack') || k.startsWith('ability') || k.startsWith('effect') || k.startsWith('rule') || k.startsWith('desc')) &&
        k !== 'card' && k !== 'ability' && k !== 'effect' && k !== 'rule'
      ) {
        const item = val[k];
        if (typeof item === 'string' && item.trim()) {
          parts.push(item.replace(/<br\s*\/?>/gi, '\n').trim());
        } else if (item && typeof item === 'object') {
          const sub = parseCardText(item);
          if (sub) parts.push(sub);
        }
      }
    });

    if (parts.length === 0) {
      Object.values(val).forEach(v => {
        if (typeof v === 'string' && v.trim()) {
          parts.push(v.replace(/<br\s*\/?>/gi, '\n').trim());
        } else if (v && typeof v === 'object') {
          const sub = parseCardText(v);
          if (sub) parts.push(sub);
        }
      });
    }

    return parts.filter(Boolean).join('\n\n');
  }
  return String(val).trim();
};

// In-memory cache for search results to avoid sluggish repeated network requests
interface SearchCacheEntry {
  timestamp: number;
  result: { data: any[]; total: number; totalPages: number };
}
const searchResultsCache = new Map<string, SearchCacheEntry>();
const SEARCH_CACHE_TTL = 30 * 1000; // 30 seconds

export interface BulkCardsResult {
  method: 'code' | 'name' | 'id';
  count: number;
  found: number;
  not_found: string[];
  data: Array<{
    query: string;
    data: any | null;
  }>;
}

export interface BulkV2CardsResult {
  count: number;
  found: number;
  not_found: any[];
  data: Array<{
    query: any;
    data: any | null;
  }>;
}

export const extractCardLowestRawPrice = (c: any): number => {
  if (!c) return 0;

  const validPrices: number[] = [];

  // Top-level direct card prices
  const topPrices = [c.price, c.MarketPrice, c.marketPrice];
  for (const tp of topPrices) {
    if (tp !== undefined && tp !== null && !isNaN(Number(tp)) && Number(tp) > 0) {
      validPrices.push(Number(tp));
    }
  }

  // Variants list (handling variants, editions, result_editions)
  const variants = Array.isArray(c.variants) && c.variants.length > 0 
    ? c.variants 
    : (Array.isArray(c.editions) && c.editions.length > 0 ? c.editions : (Array.isArray(c.result_editions) ? c.result_editions : []));

  for (const v of variants) {
    if (!v) continue;
    const rawVPrice = v.price ?? v.marketPrice ?? v.set_price ?? v.midPrice ?? v.avgPrice ?? v.lowPrice;
    if (rawVPrice !== undefined && rawVPrice !== null && !isNaN(Number(rawVPrice)) && Number(rawVPrice) > 0) {
      validPrices.push(Number(rawVPrice));
    }
  }

  // Fallback to card-level lowPrice if no other price was found
  if (validPrices.length === 0 && c.lowPrice !== undefined && c.lowPrice !== null && !isNaN(Number(c.lowPrice)) && Number(c.lowPrice) > 0) {
    validPrices.push(Number(c.lowPrice));
  }

  if (validPrices.length === 0) {
    return 0;
  }

  return Math.min(...validPrices);
};

export const fetchBulkCards = async (
  game: string,
  method: 'code' | 'name' | 'id',
  values: string[]
): Promise<BulkCardsResult | null> => {
  if (!values || values.length === 0) {
    return { method, count: 0, found: 0, not_found: [], data: [] };
  }

  try {
    const cardGames = await getCardgames();
    const gameDef = findGame(game);
    const gameData = (cardGames as any[]).find((g: any) => isGame(g.id || g.slug, game)) || gameDef;

    const apiSlug = gameDef?.slug || getGameSlug(game, 'one-piece');
    const apiBaseUrl = getGameApiBaseUrl(gameData?.sauce || gameDef?.sauce);
    const token = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_HOMURA_TOKEN) ||
                  (typeof process !== 'undefined' && (process.env?.VITE_HOMURA_TOKEN || process.env?.HOMURA_TOKEN)) || '';

    // Chunk in batches of 60 to prevent oversized requests
    const CHUNK_SIZE = 60;
    const chunks: string[][] = [];
    for (let i = 0; i < values.length; i += CHUNK_SIZE) {
      chunks.push(values.slice(i, i + CHUNK_SIZE));
    }

    const aggregatedResult: BulkCardsResult = {
      method,
      count: 0,
      found: 0,
      not_found: [],
      data: []
    };

    for (const chunk of chunks) {
      const url = `${apiBaseUrl}/api/${apiSlug}/cards/bulk`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          method,
          values: chunk
        })
      });

      if (!response.ok) {
        console.warn(`fetchBulkCards returned status ${response.status} for ${url}`);
        return null;
      }

      const resJson: BulkCardsResult = await response.json();
      aggregatedResult.count += resJson.count || chunk.length;
      aggregatedResult.found += resJson.found || 0;
      if (Array.isArray(resJson.not_found)) {
        aggregatedResult.not_found.push(...resJson.not_found);
      }
      if (Array.isArray(resJson.data)) {
        aggregatedResult.data.push(...resJson.data);
      }
    }

    return aggregatedResult;
  } catch (err) {
    console.error('Error in fetchBulkCards:', err);
    return null;
  }
};

export const fetchBulkV2Cards = async (
  game: string,
  values: Array<Record<string, any>>
): Promise<BulkV2CardsResult | null> => {
  if (!values || values.length === 0) {
    return { count: 0, found: 0, not_found: [], data: [] };
  }

  try {
    const cardGames = await getCardgames();
    const gameDef = findGame(game);
    const gameData = (cardGames as any[]).find((g: any) => isGame(g.id || g.slug, game)) || gameDef;

    const apiSlug = gameDef?.slug || getGameSlug(game, 'one-piece');
    const apiBaseUrl = getGameApiBaseUrl(gameData?.sauce || gameDef?.sauce);
    const token = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_HOMURA_TOKEN) ||
                  (typeof process !== 'undefined' && (process.env?.VITE_HOMURA_TOKEN || process.env?.HOMURA_TOKEN)) || '';

    // Chunk in batches of 60 to prevent oversized requests
    const CHUNK_SIZE = 60;
    const chunks: Array<Record<string, any>[]> = [];
    for (let i = 0; i < values.length; i += CHUNK_SIZE) {
      chunks.push(values.slice(i, i + CHUNK_SIZE));
    }

    const aggregatedResult: BulkV2CardsResult = {
      count: 0,
      found: 0,
      not_found: [],
      data: []
    };

    for (const chunk of chunks) {
      const url = `${apiBaseUrl}/api/${apiSlug}/cards/bulk/v2`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          values: chunk
        })
      });

      if (!response.ok) {
        console.warn(`fetchBulkV2Cards returned status ${response.status} for ${url}`);
        return null;
      }

      const resJson: BulkV2CardsResult = await response.json();
      aggregatedResult.count += resJson.count || chunk.length;
      aggregatedResult.found += resJson.found || 0;
      if (Array.isArray(resJson.not_found)) {
        aggregatedResult.not_found.push(...resJson.not_found);
      }
      if (Array.isArray(resJson.data)) {
        aggregatedResult.data.push(...resJson.data);
      }
    }

    return aggregatedResult;
  } catch (err) {
    console.error('Error in fetchBulkV2Cards:', err);
    return null;
  }
};

export const searchExternalCards = async (game: string, query: string, page: number = 1, limit: number = 20, filters: Record<string, string> = {}): Promise<{ data: any[], total: number, totalPages: number }> => {
  const cacheKey = `${game}_${(query || '').trim().toLowerCase()}_${page}_${limit}_${JSON.stringify(filters || {})}`;
  const cached = searchResultsCache.get(cacheKey);
  if (cached && (Date.now() - cached.timestamp < SEARCH_CACHE_TTL)) {
    return cached.result;
  }

  try {
    const cardGames = await getCardgames();
    const gameDef = findGame(game);
    const gameData = (cardGames as any[]).find((g: any) => isGame(g.id || g.slug, game)) || gameDef;

    const gameId = gameData?.id || gameDef?.id || getGameId(game, 1);
    const apiSlug = gameDef?.slug || getGameSlug(game, 'one-piece');

    let externalData: any[] = [];
    let total = 0;

    const extraFilters: Record<string, string> = {};
    if (filters && filters.set) {
      extraFilters.set = filters.set;
    }
    if (filters && filters.code) {
      extraFilters.code = filters.code;
    }

    // Clean up filters to avoid passing empty string parameters
    const cleanFilters: Record<string, string> = {};
    if (filters) {
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null && val !== '') {
          cleanFilters[key] = val;
        }
      });
    }

    const trimmedQuery = (query || '').trim();
    const isYugiohGame = isGame(apiSlug, 'yugioh');
    const isRiftbound = isGame(apiSlug, 'riftbound');
    const isHololive = isGame(apiSlug, 'hololive');
    const isGodzilla = isGame(apiSlug, 'godzilla');
    const isAltered = isGame(apiSlug, 'altered');
    const isGrandArchive = isGame(apiSlug, 'grand-archive');
    const isVanguard = isGame(apiSlug, 'cardfight-vanguard');
    const isLorcana = isGame(apiSlug, 'lorcana');
    const isGundam = isGame(apiSlug, 'gundam');
    const isUniversus = isGame(apiSlug, 'universus');
    const isPokemon = isPokemonGame(apiSlug);
    const isSorcery = isGame(apiSlug, 'sorcery');
    const isCodePattern = /^[A-Za-z0-9]+-[A-Za-z0-9]+$/i.test(trimmedQuery) || 
                          /^[A-Z]{2,4}\d{1,4}/i.test(trimmedQuery) ||
                          /^\d{4,10}$/.test(trimmedQuery);

    const apiBaseUrl = getGameApiBaseUrl(gameData?.sauce || gameDef?.sauce);
    const isFab = isGame(apiSlug, 'fab');

    if (isFab) {
      // Flesh and Blood: independente da categoria na interface (cardType, class, talent, subtype),
      // os parâmetros oficiais de consulta e filtragem para a API externa são 'type' (OR) e 'type__all' (AND).
      const fabTypeValues: string[] = [];
      const fabTypeAllValues: string[] = [];
      ['type', 'cardType', 'class', 'talent', 'talents', 'subtype', 'subtypes'].forEach(k => {
        if (cleanFilters[k]) {
          cleanFilters[k].split(',').forEach(val => fabTypeValues.push(val.trim()));
          delete cleanFilters[k];
        }
        if (cleanFilters[`${k}__all`]) {
          cleanFilters[`${k}__all`].split(',').forEach(val => fabTypeAllValues.push(val.trim()));
          delete cleanFilters[`${k}__all`];
        }
      });
      if (fabTypeAllValues.length > 0) {
        cleanFilters.type__all = Array.from(new Set(fabTypeAllValues)).join(',');
      }
      if (fabTypeValues.length > 0) {
        cleanFilters.type = Array.from(new Set(fabTypeValues)).join(',');
      }
    }

    // Separate filters supported by Homura backend vs client-side filters
    const clientSideFilters: Record<string, string> = {};
    const backendFilters: Record<string, string> = {};

    if (isPokemon) {
      Object.entries(cleanFilters).forEach(([k, v]) => {
        // Homura Pokemon backend supports: category, pokemonType, stage, rarity, hp, illustrator, set, code, sort, order, name, q
        // Client-side handles: retreat, weaknesses, resistances, weakness, resistance, excludeType
        if (['retreat', 'weaknesses', 'resistances', 'weakness', 'resistance', 'excludeType'].includes(k)) {
          clientSideFilters[k] = v;
        } else {
          backendFilters[k] = v;
        }
      });
    } else {
      Object.entries(cleanFilters).forEach(([k, v]) => {
        if (k === 'excludeType') {
          clientSideFilters[k] = v;
        } else {
          backendFilters[k] = v;
        }
      });
    }

    const hasClientSideFiltering = Object.keys(clientSideFilters).length > 0;
    const fetchLimit = hasClientSideFiltering ? Math.max(limit * 4, 100) : limit;
    const fetchPage = hasClientSideFiltering ? 1 : page;

    const buildUrlWithParams = (paramObj: Record<string, string>) => {
      const url = new URL(`${apiBaseUrl}/api/${apiSlug}/cards`);
      url.searchParams.set('page', fetchPage.toString());
      url.searchParams.set('limit', fetchLimit.toString());

      const combined: Record<string, string> = {
        ...backendFilters,
        ...extraFilters,
        ...paramObj
      };

      Object.entries(combined).forEach(([key, val]) => {
        if (val === undefined || val === null || val === '') return;
        const sVal = String(val).trim();
        // Conforme o guia oficial da API Homura:
        // Múltiplos valores podem ser passados repetindo o parâmetro (campo=val1&campo=val2)
        // ou separados por vírgula (campo=val1,val2). Repetir o parâmetro com append garante compatibilidade total.
        if (sVal.includes(',')) {
          const parts = sVal.split(',').map(s => s.trim()).filter(Boolean);
          parts.forEach(p => url.searchParams.append(key, p));
        } else {
          url.searchParams.append(key, sVal);
        }
      });

      return url.toString();
    };

    const token = (typeof import.meta !== 'undefined' && import.meta?.env?.VITE_HOMURA_TOKEN) || (typeof process !== 'undefined' && process.env?.VITE_HOMURA_TOKEN) || '';
    const fetchFromHomura = async (url: string) => {
      try {
        const response = await fetch(url, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const json = await response.json();
          return json;
        }
        // If Homura returns 400 with unsupported filters, strip them and retry
        if (response.status === 400) {
          const errData = await response.json().catch(() => null);
          if (errData?.detail?.filters && Array.isArray(errData.detail.filters)) {
            const parsedUrl = new URL(url);
            let strippedAny = false;
            errData.detail.filters.forEach((badFilter: string) => {
              if (parsedUrl.searchParams.has(badFilter)) {
                parsedUrl.searchParams.delete(badFilter);
                strippedAny = true;
              }
            });
            if (strippedAny) {
              const retryRes = await fetch(parsedUrl.toString(), {
                headers: {
                  'Authorization': token ? `Bearer ${token}` : '',
                  'Content-Type': 'application/json'
                }
              });
              if (retryRes.ok) {
                return await retryRes.json();
              }
            }
          }
        }
      } catch (e) {
        console.warn('Homura fetch error:', e);
      }
      return null;
    };

    let result: any = { data: [], total: 0 };

    if (trimmedQuery) {
      if (isCodePattern) {
        // Strategy 1: Search by code / id / number / name
        let res = await fetchFromHomura(buildUrlWithParams({ code: trimmedQuery }));
        if (!res || !res.data || res.data.length === 0) {
          res = await fetchFromHomura(buildUrlWithParams({ id: trimmedQuery }));
        }
        if (!res || !res.data || res.data.length === 0) {
          res = await fetchFromHomura(buildUrlWithParams({ number: trimmedQuery }));
        }
        if (!res || !res.data || res.data.length === 0) {
          res = await fetchFromHomura(buildUrlWithParams({ name: trimmedQuery }));
        }
        if (res && res.data && res.data.length > 0) {
          result = res;
        } else {
          result = { data: [], total: 0 };
        }
      } else {
        // Strategy 2: Search by name, then code / id if needed
        let res = await fetchFromHomura(buildUrlWithParams({ name: trimmedQuery }));
        if (!res || !res.data || res.data.length === 0) {
          res = await fetchFromHomura(buildUrlWithParams({ code: trimmedQuery }));
        }
        if (!res || !res.data || res.data.length === 0) {
          res = await fetchFromHomura(buildUrlWithParams({ id: trimmedQuery }));
        }
        if (res && res.data && res.data.length > 0) {
          result = res;
        } else {
          result = { data: [], total: 0 };
        }
      }
    } else {
      // Empty query with filters
      const res = await fetchFromHomura(buildUrlWithParams({}));
      if (res) result = res;
    }

    const list = Array.isArray(result) ? result : (result.data || []);
    
    if (list.length > 0) {
      let mapped = list.map((c: any) => parseHomuraCard(c, {
        game: apiSlug,
        gameName: gameData?.name || game,
        gameId,
        cardBackUrl: gameData?.card_back_url
      }));

      // Se houver uma query de texto ativa, valida com rigor se as cartas retornadas realmente contêm o termo
      // Isso impede que qualquer fallback da API retorne todas as cartas quando nada for encontrado
      if (trimmedQuery) {
        const qLower = trimmedQuery.toLowerCase();
        mapped = mapped.filter((card: any) =>
          card.name?.toLowerCase().includes(qLower) ||
          card.code?.toLowerCase().includes(qLower) ||
          card.id?.toLowerCase().includes(qLower) ||
          card.number?.toLowerCase().includes(qLower)
        );
      }

      if (hasClientSideFiltering) {
        if (clientSideFilters.searchQuery) {
          const q = clientSideFilters.searchQuery.toLowerCase();
          mapped = mapped.filter((card: any) =>
            card.name?.toLowerCase().includes(q) ||
            card.code?.toLowerCase().includes(q) ||
            card.id?.toLowerCase().includes(q) ||
            card.number?.toLowerCase().includes(q)
          );
        }
        if (clientSideFilters.type || clientSideFilters.cardType) {
          const rawTarget = String(clientSideFilters.type || clientSideFilters.cardType).toLowerCase();
          const targets = rawTarget.split(/[,|]/).map((t: string) => t.trim()).filter(Boolean);
          mapped = mapped.filter((card: any) => {
            const cType = String(card.type || '').toLowerCase();
            const cCardType = String(card.cardType || card.card_type || '').toLowerCase();
            const cTypes = Array.isArray(card.types) ? card.types.map((t: any) => String(t).toLowerCase()) : [];
            const cClasses = Array.isArray(card.classes) ? card.classes.map((t: any) => String(t).toLowerCase()) : [];
            const cSubtypes = Array.isArray(card.subtypes) ? card.subtypes.map((t: any) => String(t).toLowerCase()) : [];
            const cTalents = Array.isArray(card.talents) ? card.talents.map((t: any) => String(t).toLowerCase()) : [];

            const checkMatch = (t: string) => (
              cType === t || cType.includes(t) ||
              cCardType === t || cCardType.includes(t) ||
              cTypes.some((ct: string) => ct === t || ct.includes(t)) ||
              cClasses.some((ct: string) => ct === t || ct.includes(t)) ||
              cSubtypes.some((ct: string) => ct === t || ct.includes(t)) ||
              cTalents.some((ct: string) => ct === t || ct.includes(t))
            );

            return isFab ? targets.every(checkMatch) : targets.some(checkMatch);
          });
        }
        if (clientSideFilters.excludeType) {
          const rawTarget = String(clientSideFilters.excludeType).toLowerCase();
          const targets = rawTarget.split(/[,|]/).map((t: string) => t.trim()).filter(Boolean);
          mapped = mapped.filter((card: any) => {
            const cType = String(card.type || '').toLowerCase();
            const cCardType = String(card.cardType || card.card_type || '').toLowerCase();
            const cTypes = Array.isArray(card.types) ? card.types.map((t: any) => String(t).toLowerCase()) : [];
            return !targets.some((t: string) =>
              cType === t || cType.includes(t) ||
              cCardType === t || cCardType.includes(t) ||
              cTypes.some((ct: string) => ct === t || ct.includes(t))
            );
          });
        }
        if (clientSideFilters.super) {
          const target = clientSideFilters.super.toLowerCase();
          mapped = mapped.filter((card: any) =>
            card.super?.toLowerCase() === target ||
            card.cardType?.toLowerCase().includes(target)
          );
        }
        if (clientSideFilters.color || clientSideFilters.colors || clientSideFilters.domain) {
          const target = (clientSideFilters.color || clientSideFilters.colors || clientSideFilters.domain).toLowerCase();
          mapped = mapped.filter((card: any) => {
            const cStr = Array.isArray(card.color) ? card.color.join(' ') : String(card.color || '');
            const domStr = String(card.domain || '');
            const colsArr = Array.isArray(card.colors) ? card.colors : [];
            return (
              cStr.toLowerCase().includes(target) ||
              domStr.toLowerCase().includes(target) ||
              colsArr.some((col: any) => String(col).toLowerCase().includes(target))
            );
          });
        }
        if (clientSideFilters.rarity) {
          const target = clientSideFilters.rarity.toLowerCase().replace(/\s+/g, '');
          mapped = mapped.filter((card: any) =>
            card.rarity?.toLowerCase().replace(/\s+/g, '') === target
          );
        }
        if (clientSideFilters.bloomLevel) {
          const target = clientSideFilters.bloomLevel.toLowerCase();
          mapped = mapped.filter((card: any) =>
            card.bloomLevel?.toLowerCase() === target
          );
        }
        if (clientSideFilters.grade) {
          const target = clientSideFilters.grade.toLowerCase();
          mapped = mapped.filter((card: any) =>
            String(card.grade !== null && card.grade !== undefined ? card.grade : '').toLowerCase() === target
          );
        }
        if (clientSideFilters.nation || clientSideFilters.clan) {
          const target = (clientSideFilters.nation || clientSideFilters.clan).toLowerCase();
          mapped = mapped.filter((card: any) => {
            const nStr = String(card.nation || '').toLowerCase();
            const cStr = String(card.clan || '').toLowerCase();
            return nStr === target || nStr.includes(target) || cStr === target || cStr.includes(target);
          });
        }
        if (clientSideFilters.shield) {
          const target = String(clientSideFilters.shield).trim();
          mapped = mapped.filter((card: any) => String(card.shield) === target);
        }
        if (clientSideFilters.faction) {
          const target = clientSideFilters.faction.toLowerCase();
          mapped = mapped.filter((card: any) => {
            const fName = typeof card.faction === 'object' ? card.faction?.name : String(card.faction || '');
            const fCode = typeof card.faction === 'object' ? card.faction?.code : '';
            return (
              fName?.toLowerCase() === target ||
              fName?.toLowerCase().includes(target) ||
              (fCode && fCode.toLowerCase() === target)
            );
          });
        }
        if (clientSideFilters.mainCost) {
          const target = String(clientSideFilters.mainCost).trim();
          mapped = mapped.filter((card: any) => String(card.mainCost) === target || String(card.cost) === target);
        }
        if (clientSideFilters.recallCost) {
          const target = String(clientSideFilters.recallCost).trim();
          mapped = mapped.filter((card: any) => String(card.recallCost) === target);
        }
        if (clientSideFilters.collector_number) {
          const target = String(clientSideFilters.collector_number).trim().toLowerCase();
          mapped = mapped.filter((card: any) => 
            String(card.collector_number || '').toLowerCase().includes(target)
          );
        }
        if (clientSideFilters.cardType) {
          const target = clientSideFilters.cardType.toLowerCase();
          mapped = mapped.filter((card: any) =>
            card.cardType?.toLowerCase().includes(target) ||
            card.type?.toLowerCase() === target ||
            card.super?.toLowerCase() === target
          );
        }
        if (clientSideFilters.element) {
          const target = clientSideFilters.element.toLowerCase();
          mapped = mapped.filter((card: any) => {
            const elStr = String(card.element || '').toLowerCase();
            const elsArr = Array.isArray(card.elements) ? card.elements : [];
            return elStr === target || elStr.includes(target) || elsArr.some((e: any) => String(e).toLowerCase().includes(target));
          });
        }
        if (clientSideFilters.class || clientSideFilters.classes) {
          const target = (clientSideFilters.class || clientSideFilters.classes).toLowerCase();
          mapped = mapped.filter((card: any) => {
            const clStr = String(card.class || '').toLowerCase();
            const clsArr = Array.isArray(card.classes) ? card.classes : [];
            const subArr = Array.isArray(card.subtypes) ? card.subtypes : [];
            return clStr === target || clStr.includes(target) ||
                   clsArr.some((c: any) => String(c).toLowerCase().includes(target)) ||
                   subArr.some((s: any) => String(s).toLowerCase().includes(target));
          });
        }
        if (clientSideFilters.cost_memory) {
          const target = String(clientSideFilters.cost_memory).trim();
          mapped = mapped.filter((card: any) => String(card.cost_memory) === target);
        }
        if (clientSideFilters.cost_reserve) {
          const target = String(clientSideFilters.cost_reserve).trim();
          mapped = mapped.filter((card: any) => String(card.cost_reserve) === target);
        }
        if (clientSideFilters.level) {
          const target = String(clientSideFilters.level).trim();
          mapped = mapped.filter((card: any) => String(card.level) === target);
        }
        if (clientSideFilters.cost) {
          const target = String(clientSideFilters.cost).trim();
          mapped = mapped.filter((card: any) => String(card.cost) === target || String(card.energy) === target);
        }
        if (clientSideFilters.inkable !== undefined && clientSideFilters.inkable !== null && clientSideFilters.inkable !== '') {
          const target = String(clientSideFilters.inkable).toLowerCase();
          mapped = mapped.filter((card: any) => String(card.inkable).toLowerCase() === target);
        }
        if (clientSideFilters.lore !== undefined && clientSideFilters.lore !== null && clientSideFilters.lore !== '') {
          const target = String(clientSideFilters.lore).trim();
          mapped = mapped.filter((card: any) => String(card.lore) === target);
        }
        if (clientSideFilters.strength !== undefined && clientSideFilters.strength !== null && clientSideFilters.strength !== '') {
          const target = String(clientSideFilters.strength).trim();
          mapped = mapped.filter((card: any) => String(card.strength) === target || String(card.power) === target);
        }
        if (clientSideFilters.willpower !== undefined && clientSideFilters.willpower !== null && clientSideFilters.willpower !== '') {
          const target = String(clientSideFilters.willpower).trim();
          mapped = mapped.filter((card: any) => String(card.willpower) === target || String(card.defense) === target);
        }
        if (clientSideFilters.classifications) {
          const target = clientSideFilters.classifications.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.classifications || '').toLowerCase().includes(target));
        }
        if (clientSideFilters.ap !== undefined && clientSideFilters.ap !== null && clientSideFilters.ap !== '') {
          const target = String(clientSideFilters.ap).trim();
          mapped = mapped.filter((card: any) => String(card.ap) === target || String(card.power) === target);
        }
        if (clientSideFilters.hp !== undefined && clientSideFilters.hp !== null && clientSideFilters.hp !== '') {
          const target = String(clientSideFilters.hp).trim();
          mapped = mapped.filter((card: any) => String(card.hp) === target || String(card.defense) === target);
        }
        if (clientSideFilters.zone) {
          const target = clientSideFilters.zone.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.zone || '').toLowerCase().includes(target));
        }
        if (clientSideFilters.trait) {
          const target = clientSideFilters.trait.toLowerCase();
          mapped = mapped.filter((card: any) =>
            String(card.trait || '').toLowerCase().includes(target) ||
            (Array.isArray(card.traits) && card.traits.some((t: any) => String(t).toLowerCase().includes(target)))
          );
        }
        if (clientSideFilters.link) {
          const target = clientSideFilters.link.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.link || '').toLowerCase().includes(target));
        }
        if (clientSideFilters.source_title || clientSideFilters.sourceTitle) {
          const target = (clientSideFilters.source_title || clientSideFilters.sourceTitle).toLowerCase();
          mapped = mapped.filter((card: any) => String(card.source_title || '').toLowerCase().includes(target));
        }
        if (clientSideFilters.icons || clientSideFilters.icon) {
          const target = String(clientSideFilters.icons || clientSideFilters.icon).toLowerCase();
          mapped = mapped.filter((card: any) =>
            (Array.isArray(card.icons) && card.icons.some((i: any) => String(i).toLowerCase().includes(target))) ||
            (Array.isArray(card.elements) && card.elements.some((i: any) => String(i).toLowerCase().includes(target))) ||
            String(card.domain || '').toLowerCase().includes(target)
          );
        }
        if (clientSideFilters.difficulty !== undefined && clientSideFilters.difficulty !== null && clientSideFilters.difficulty !== '') {
          const target = String(clientSideFilters.difficulty).trim();
          mapped = mapped.filter((card: any) => String(card.difficulty) === target || String(card.cost) === target);
        }
        if (clientSideFilters.control !== undefined && clientSideFilters.control !== null && clientSideFilters.control !== '') {
          const target = String(clientSideFilters.control).trim();
          mapped = mapped.filter((card: any) => String(card.control) === target);
        }
        if (clientSideFilters.block !== undefined && clientSideFilters.block !== null && clientSideFilters.block !== '') {
          const target = String(clientSideFilters.block).trim();
          mapped = mapped.filter((card: any) => String(card.block) === target);
        }
        if (clientSideFilters.block_zone) {
          const target = clientSideFilters.block_zone.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.block_zone || '').toLowerCase().includes(target));
        }
        if (clientSideFilters.vitality !== undefined && clientSideFilters.vitality !== null && clientSideFilters.vitality !== '') {
          const target = String(clientSideFilters.vitality).trim();
          mapped = mapped.filter((card: any) => String(card.vitality) === target);
        }
        if (clientSideFilters.hand_size !== undefined && clientSideFilters.hand_size !== null && clientSideFilters.hand_size !== '') {
          const target = String(clientSideFilters.hand_size).trim();
          mapped = mapped.filter((card: any) => String(card.hand_size) === target);
        }
        if (clientSideFilters.retreat !== undefined && clientSideFilters.retreat !== null && clientSideFilters.retreat !== '') {
          const target = String(clientSideFilters.retreat).trim();
          mapped = mapped.filter((card: any) => String(card.retreat) === target);
        }
        if (clientSideFilters.weaknesses || clientSideFilters.weakness) {
          const target = String(clientSideFilters.weaknesses || clientSideFilters.weakness).trim().toLowerCase();
          mapped = mapped.filter((card: any) =>
            String(card.weaknesses || card.weakness || '').toLowerCase().includes(target)
          );
        }
        if (clientSideFilters.resistances || clientSideFilters.resistance) {
          const target = String(clientSideFilters.resistances || clientSideFilters.resistance).trim().toLowerCase();
          mapped = mapped.filter((card: any) =>
            String(card.resistances || card.resistance || '').toLowerCase().includes(target)
          );
        }
        if (clientSideFilters.category) {
          const target = clientSideFilters.category.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.category || '').toLowerCase() === target);
        }
        if (clientSideFilters.pokemonType) {
          const target = clientSideFilters.pokemonType.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.pokemonType || '').toLowerCase() === target);
        }
        if (clientSideFilters.stage) {
          const target = clientSideFilters.stage.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.stage || '').toLowerCase() === target);
        }
        if (clientSideFilters.illustrator) {
          const target = clientSideFilters.illustrator.toLowerCase();
          mapped = mapped.filter((card: any) => String(card.illustrator || '').toLowerCase().includes(target));
        }
        total = mapped.length;
        const startIndex = (page - 1) * limit;
        externalData = mapped.slice(startIndex, startIndex + limit);
      } else {
        externalData = mapped;
        const serverTotal = result.total ?? result.total_cards ?? result.totalCount;
        if (typeof serverTotal === 'number' && serverTotal >= 0) {
          total = (page === 1 && mapped.length === 0) ? 0 : serverTotal;
        } else {
          total = (page === 1 && mapped.length === 0) 
            ? 0 
            : Math.max(externalData.length, (page - 1) * limit + externalData.length);
        }
      }
    } else {
      externalData = [];
      total = 0;
    }

    const searchResult = {
      data: externalData,
      total: total,
      totalPages: total > 0 ? Math.ceil(total / limit) : 0
    };

    if (externalData.length > 0) {
      // Limit cache size to prevent memory bloat
      if (searchResultsCache.size > 80) {
        const firstKey = searchResultsCache.keys().next().value;
        if (firstKey) searchResultsCache.delete(firstKey);
      }
      searchResultsCache.set(cacheKey, { timestamp: Date.now(), result: searchResult });
    }

    return searchResult;
  } catch (error) {
    console.error('Error searching cards:', error);
    return { data: [], total: 0, totalPages: 0 };
  }
};


export interface FoldersStatsResult {
  system: { colecao: number; wishlist: number; offerlist: number };
  binders: Record<string, number>;
  estimatedValues: {
    system: { colecao: number; wishlist: number; offerlist: number };
    binders: Record<string, number>;
  };
}

const FOLDERS_STATS_CACHE_KEY_PREFIX = 'cardumy_folders_stats_v2_';
const FOLDERS_STATS_TTL = 15 * 60 * 1000; // 15 minutos de TTL

const inMemoryFolderStatsCache = new Map<string, { timestamp: number; data: FoldersStatsResult }>();
const inFlightFolderStatsPromises = new Map<string, Promise<FoldersStatsResult>>();

/**
 * Retorna os dados em cache de contagem e valor estimado das pastas imediatamente, se existirem.
 * Lê primeiro da memória RAM (0ms) e, se não encontrar, do localStorage.
 */
export const getCachedFoldersStats = (userId: string | number): FoldersStatsResult | null => {
  if (!userId) return null;
  const userKey = String(userId);

  // 1. Memória RAM
  const mem = inMemoryFolderStatsCache.get(userKey);
  if (mem && mem.data) {
    return mem.data;
  }

  // 2. LocalStorage
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = localStorage.getItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${userKey}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.data) {
          inMemoryFolderStatsCache.set(userKey, { timestamp: parsed.timestamp || Date.now(), data: parsed.data });
          return parsed.data;
        }
      }
    } catch (err) {
      console.debug('Erro ao ler cache de stats das pastas:', err);
    }
  }

  return null;
};

/**
 * Invalida o cache de estatísticas de pastas de um usuário específico ou de todos.
 * Útil quando o usuário adiciona/remove cartas, cria/exclui pastas ou força atualização.
 */
export const invalidateFoldersStatsCache = (userId?: string | number): void => {
  if (userId) {
    const userKey = String(userId);
    inMemoryFolderStatsCache.delete(userKey);
    inFlightFolderStatsPromises.delete(userKey);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        localStorage.removeItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${userKey}`);
        // Também remove chaves de prefixo para garantir limpeza de chaves associadas
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith(FOLDERS_STATS_CACHE_KEY_PREFIX)) {
            localStorage.removeItem(k);
          }
        }
      } catch {}
    }
  } else {
    inMemoryFolderStatsCache.clear();
    inFlightFolderStatsPromises.clear();
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const key = localStorage.key(i);
          if (key && key.startsWith(FOLDERS_STATS_CACHE_KEY_PREFIX)) {
            localStorage.removeItem(key);
          }
        }
      } catch {}
    }
  }
};

export const getFoldersStats = async (
  userId: string | number,
  forceRefresh: boolean = false
): Promise<FoldersStatsResult> => {
  try {
    let resolvedUserId = userId;
    if (typeof userId === 'string' && userId.includes('-')) {
        const u = await getUserByAuthId(userId);
        if (u) resolvedUserId = u.id;
    }
    const userKey = String(resolvedUserId);
    const rawKey = String(userId);

    // Se for forceRefresh, limpa caches e cancela deduplicação anterior
    if (forceRefresh) {
      inMemoryFolderStatsCache.delete(userKey);
      inMemoryFolderStatsCache.delete(rawKey);
      inFlightFolderStatsPromises.delete(userKey);
      inFlightFolderStatsPromises.delete(rawKey);
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          localStorage.removeItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${userKey}`);
          localStorage.removeItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${rawKey}`);
        } catch {}
      }
    } else {
      // 1. Verificação de Cache recente (se não for forceRefresh)
      const mem = inMemoryFolderStatsCache.get(userKey) || inMemoryFolderStatsCache.get(rawKey);
      if (mem && (Date.now() - mem.timestamp < FOLDERS_STATS_TTL)) {
        return mem.data;
      }

      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          const raw = localStorage.getItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${userKey}`) || 
                      localStorage.getItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${rawKey}`);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && parsed.data && (Date.now() - (parsed.timestamp || 0) < FOLDERS_STATS_TTL)) {
              inMemoryFolderStatsCache.set(userKey, { timestamp: parsed.timestamp, data: parsed.data });
              inMemoryFolderStatsCache.set(rawKey, { timestamp: parsed.timestamp, data: parsed.data });
              return parsed.data;
            }
          }
        } catch (e) {
          // ignora falha de parse
        }
      }
    }

    // 2. Deduplicação de requisições simultâneas em andamento
    if (!forceRefresh && inFlightFolderStatsPromises.has(userKey)) {
      return await inFlightFolderStatsPromises.get(userKey)!;
    }

    const executionPromise = (async (): Promise<FoldersStatsResult> => {
      const stats: { colecao: number; wishlist: number; offerlist: number } = {
        colecao: 0,
        wishlist: 0,
        offerlist: 0
      };

      const estimatedValues: {
        system: { colecao: number; wishlist: number; offerlist: number };
        binders: Record<string, number>;
      } = {
        system: { colecao: 0, wishlist: 0, offerlist: 0 },
        binders: {}
      };

      // Se for forceRefresh, desativa preferStoredPrices para recalcular e atualizar com os preços mais recentes
      const preferStored = !forceRefresh;

      // System counts and card references
      const [collecaoRes, wishlistRes, offerlistRes] = await Promise.all([
        supabase
          .from('user_collection')
          .select('id, quantity, game_id, cards:card_id (id, name, external_id, game_id, price, price_updated_at)')
          .eq('user_id', resolvedUserId),
        supabase
          .from('user_wishlist')
          .select('id, quantity, game_id, cards:card_id (id, name, external_id, game_id, price, price_updated_at)')
          .eq('user_id', resolvedUserId),
        supabase
          .from('user_offerlist')
          .select('id, quantity, game_id, cards:card_id (id, name, external_id, game_id, price, price_updated_at)')
          .eq('user_id', resolvedUserId)
      ]);

      const collecaoData = collecaoRes.data || [];
      const wishlistData = wishlistRes.data || [];
      const offerlistData = offerlistRes.data || [];

      stats.colecao = collecaoData.length;
      stats.wishlist = wishlistData.length;
      stats.offerlist = offerlistData.length;

      // Binder counts & items
      const { data: binders } = await supabase.from('user_binders').select('id, name, game_id').eq('user_id', resolvedUserId);
      const binderStatsMap: Record<string, number> = {};

      if (binders && binders.length > 0) {
        const binderIds = binders.map(b => b.id);
        binderIds.forEach(id => {
          binderStatsMap[id] = 0;
          estimatedValues.binders[id] = 0;
        });

        const { data: binderCards } = await supabase
          .from('binder_cards')
          .select(`
            id,
            binder_id,
            card_id,
            cards:card_id (
              id,
              name,
              external_id,
              game_id,
              price,
              price_updated_at
            )
          `)
          .in('binder_id', binderIds);

        const binderItemsMap: Record<string, CardPriceItem[]> = {};
        binderIds.forEach(id => {
          binderItemsMap[id] = [];
        });

        (binderCards || []).forEach((bc: any) => {
          const bId = bc.binder_id;
          const c = Array.isArray(bc.cards) ? bc.cards[0] : bc.cards;
          if (bId) {
            binderStatsMap[bId] = (binderStatsMap[bId] || 0) + 1;
            if (binderItemsMap[bId]) {
              binderItemsMap[bId].push({
                id: c?.id,
                card_id: c?.id,
                card_db_id: c?.id,
                game_id: c?.game_id,
                external_id: c?.external_id,
                name: c?.name,
                quantity: 1,
                price: c?.price !== null && c?.price !== undefined ? convertUsdToBrl(Number(c?.price)) : null,
                price_updated_at: c?.price_updated_at || null
              });
            }
          }
        });

        // Calculate estimated value for each binder
        await Promise.all(
          binderIds.map(async (bId) => {
            const items = binderItemsMap[bId] || [];
            if (items.length > 0) {
              estimatedValues.binders[bId] = await calculateFolderEstimatedValue(items, 6, preferStored);
            }
          })
        );
      }

      // Calculate estimated values for system folders
      const toCardPriceItems = (rows: any[]): CardPriceItem[] => {
        return rows.map(r => {
          const cardObj = Array.isArray(r.cards) ? r.cards[0] : (r.cards || r.card);
          return {
            id: cardObj?.id,
            card_id: cardObj?.id,
            card_db_id: cardObj?.id,
            game_id: r.game_id || cardObj?.game_id,
            external_id: cardObj?.external_id,
            name: cardObj?.name,
            quantity: typeof r.quantity === 'number' ? r.quantity : 1,
            price: cardObj?.price !== null && cardObj?.price !== undefined ? convertUsdToBrl(Number(cardObj?.price)) : null,
            price_updated_at: cardObj?.price_updated_at || null
          };
        });
      };

      const [colVal, wishVal, offVal] = await Promise.all([
        calculateFolderEstimatedValue(toCardPriceItems(collecaoData), 6, preferStored),
        calculateFolderEstimatedValue(toCardPriceItems(wishlistData), 6, preferStored),
        calculateFolderEstimatedValue(toCardPriceItems(offerlistData), 6, preferStored)
      ]);

      estimatedValues.system.colecao = colVal;
      estimatedValues.system.wishlist = wishVal;
      estimatedValues.system.offerlist = offVal;

      const result: FoldersStatsResult = {
        system: stats,
        binders: binderStatsMap,
        estimatedValues
      };

      // Gravação em cache persistente e memória (salva tanto na chave numérica quanto na chave auth)
      inMemoryFolderStatsCache.set(userKey, { timestamp: Date.now(), data: result });
      inMemoryFolderStatsCache.set(rawKey, { timestamp: Date.now(), data: result });
      if (typeof window !== 'undefined' && window.localStorage) {
        try {
          const payload = JSON.stringify({ timestamp: Date.now(), data: result });
          localStorage.setItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${userKey}`, payload);
          localStorage.setItem(`${FOLDERS_STATS_CACHE_KEY_PREFIX}${rawKey}`, payload);
        } catch (err) {
          console.debug('Erro ao persistir cache de folders stats:', err);
        }
      }

      return result;
    })().finally(() => {
      inFlightFolderStatsPromises.delete(userKey);
      inFlightFolderStatsPromises.delete(rawKey);
    });

    inFlightFolderStatsPromises.set(userKey, executionPromise);
    inFlightFolderStatsPromises.set(rawKey, executionPromise);
    return await executionPromise;
  } catch (error) {
    console.error('Error fetching folder stats:', error);
    return {
      system: { colecao: 0, wishlist: 0, offerlist: 0 },
      binders: {},
      estimatedValues: {
        system: { colecao: 0, wishlist: 0, offerlist: 0 },
        binders: {}
      }
    };
  }
};


export const getCollectionRanking = async (limit: number = 5) => {
  try {
    const { data, error } = await supabase
      .rpc('get_collection_ranking', { p_limit: limit });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching collection ranking:', error);
    return [];
  }
};


export const getOffersRanking = async (limit: number = 5) => {
  try {
    const { data, error } = await supabase
      .rpc('get_offers_ranking', { p_limit: limit });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching offers ranking:', error);
    return [];
  }
};


export interface CardlinkMatchCard {
  id: number | string;
  name: string;
  image_url?: string | null;
  imageUrl?: string | null;
  set_code?: string | null;
  price?: number;
  game_id?: number;
}

export interface CardlinkMatch {
  matched_user_id: number;
  matched_username: string;
  matched_avatar?: string | null;
  fullname?: string | null;
  vip?: boolean;
  is_mutual: boolean;
  wishlist_matches: CardlinkMatchCard[]; // Minha wishlist que o outro usuário possui na offerlist dele
  offerlist_matches: CardlinkMatchCard[]; // Minha offerlist que o outro usuário deseja na wishlist dele
  matching_cards: CardlinkMatchCard[]; // Lista combinada de cartas para exibição rápida e seleção
  total_matches: number;
}

export const getOfferMatches = async (userId: number | string): Promise<CardlinkMatch[]> => {
  try {
    let resolvedUserId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.length > 20)) {
      const u = await getUserByAuthId(userId);
      if (u) resolvedUserId = u.id;
    }
    const numUserId = Number(resolvedUserId);
    if (!numUserId || isNaN(numUserId)) return [];

    // Busca em paralelo: minha wishlist, minha offerlist e a lista de usuários seguros da comunidade
    const [wishRes, offerRes, safeUsersRes] = await Promise.all([
      supabase
        .from('user_wishlist')
        .select('card_id, quantity, cards(id, name, image_url, set_code, price, game_id)')
        .eq('user_id', numUserId),
      supabase
        .from('user_offerlist')
        .select('card_id, quantity, cards(id, name, image_url, set_code, price, game_id)')
        .eq('user_id', numUserId),
      supabase.rpc('get_safe_users') as any
    ]);

    const myWishes = wishRes.data || [];
    const myOffers = offerRes.data || [];
    const safeUsers = safeUsersRes.data || [];
    const usersMap = new Map((safeUsers || []).map((u: any) => [Number(u.id), u]));

    const myWishCardIds = Array.from(new Set(myWishes.map((w: any) => w.card_id).filter(Boolean)));
    const myOfferCardIds = Array.from(new Set(myOffers.map((o: any) => o.card_id).filter(Boolean)));

    // Busca correspondências em ambas as direções no banco:
    // 1. Minha Wishlist na Offerlist de outros usuários (Eles têm o que eu procuro)
    // 2. Minha Offerlist na Wishlist de outros usuários (Eles querem o que eu tenho para troca)
    const [matchingOffersRes, matchingWishesRes] = await Promise.all([
      myWishCardIds.length > 0
        ? supabase
            .from('user_offerlist')
            .select('id, user_id, card_id, cards(id, name, image_url, set_code, price, game_id)')
            .in('card_id', myWishCardIds)
            .neq('user_id', numUserId)
        : Promise.resolve({ data: [] }),
      myOfferCardIds.length > 0
        ? supabase
            .from('user_wishlist')
            .select('id, user_id, card_id, cards(id, name, image_url, set_code, price, game_id)')
            .in('card_id', myOfferCardIds)
            .neq('user_id', numUserId)
        : Promise.resolve({ data: [] })
    ]);

    const matchingOffers = matchingOffersRes.data || [];
    const matchingWishes = matchingWishesRes.data || [];

    // Agrupamento unificado por usuário parceiro
    const userMatchesMap = new Map<number, {
      matched_user_id: number;
      wishlist_matches: CardlinkMatchCard[];
      offerlist_matches: CardlinkMatchCard[];
    }>();

    // 1. Preenche cartas que outros usuários possuem que estão na MINHA Wishlist
    for (const item of matchingOffers) {
      const partnerId = Number(item.user_id);
      if (!partnerId || partnerId === numUserId) continue;
      if (!userMatchesMap.has(partnerId)) {
        userMatchesMap.set(partnerId, {
          matched_user_id: partnerId,
          wishlist_matches: [],
          offerlist_matches: []
        });
      }
      const entry = userMatchesMap.get(partnerId)!;
      const rawCard = Array.isArray((item as any).cards) ? (item as any).cards[0] : (item as any).cards;
      if (rawCard && !entry.wishlist_matches.some(c => c.id === rawCard.id)) {
        entry.wishlist_matches.push({
          id: rawCard.id,
          name: rawCard.name || 'Carta sem nome',
          image_url: rawCard.image_url,
          imageUrl: rawCard.image_url,
          set_code: rawCard.set_code,
          price: rawCard.price,
          game_id: rawCard.game_id
        });
      }
    }

    // 2. Preenche cartas que outros usuários desejam que estão na MINHA Offerlist
    for (const item of matchingWishes) {
      const partnerId = Number(item.user_id);
      if (!partnerId || partnerId === numUserId) continue;
      if (!userMatchesMap.has(partnerId)) {
        userMatchesMap.set(partnerId, {
          matched_user_id: partnerId,
          wishlist_matches: [],
          offerlist_matches: []
        });
      }
      const entry = userMatchesMap.get(partnerId)!;
      const rawCard = Array.isArray((item as any).cards) ? (item as any).cards[0] : (item as any).cards;
      if (rawCard && !entry.offerlist_matches.some(c => c.id === rawCard.id)) {
        entry.offerlist_matches.push({
          id: rawCard.id,
          name: rawCard.name || 'Carta sem nome',
          image_url: rawCard.image_url,
          imageUrl: rawCard.image_url,
          set_code: rawCard.set_code,
          price: rawCard.price,
          game_id: rawCard.game_id
        });
      }
    }

    const matchesList: CardlinkMatch[] = [];
    for (const [partnerId, entry] of userMatchesMap.entries()) {
      const profile = (usersMap.get(partnerId) as any) || {};
      const isMutual = entry.wishlist_matches.length > 0 && entry.offerlist_matches.length > 0;
      
      const combinedCardsMap = new Map<any, CardlinkMatchCard>();
      for (const c of entry.wishlist_matches) combinedCardsMap.set(c.id, c);
      for (const c of entry.offerlist_matches) combinedCardsMap.set(c.id, c);

      matchesList.push({
        matched_user_id: partnerId,
        matched_username: profile.username || `user_${partnerId}`,
        matched_avatar: profile.avatar || null,
        fullname: profile.fullname || profile.username || `user_${partnerId}`,
        vip: Boolean(profile.vip),
        is_mutual: isMutual,
        wishlist_matches: entry.wishlist_matches,
        offerlist_matches: entry.offerlist_matches,
        matching_cards: Array.from(combinedCardsMap.values()),
        total_matches: entry.wishlist_matches.length + entry.offerlist_matches.length
      });
    }

    // Ordenação: trocas mútuas em primeiro lugar, depois maior volume de cartas
    matchesList.sort((a, b) => {
      if (a.is_mutual && !b.is_mutual) return -1;
      if (!a.is_mutual && b.is_mutual) return 1;
      return b.total_matches - a.total_matches;
    });

    return matchesList;
  } catch (error) {
    console.error('Error fetching offer matches:', error);
    return [];
  }
};

export const getUserCardsForCollection = async (userId: string | number, gameId?: number | string | 'All') => {
  try {
    let resolvedUserId = userId;
    if (typeof userId === 'string' && (userId.includes('-') || userId.length > 30)) {
      const u = await getUserByAuthId(userId);
      if (u) resolvedUserId = u.id;
    }

    let query = supabase
      .from('user_collection')
      .select(`
        id,
        quantity,
        game_id,
        card_id,
        condition_id,
        cards:card_id (
          id,
          name,
          external_id,
          set_code,
          variant_code,
          variant_number,
          image_url,
          game_id,
          rarity
        )
      `)
      .eq('user_id', resolvedUserId);

    if (gameId && gameId !== 'All') {
      query = query.eq('game_id', Number(gameId));
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data || []).map((item: any) => {
      const c = Array.isArray(item.cards) ? item.cards[0] : item.cards;
      const rarityName = c?.rarity || (item as any)?.raridade || undefined;

      const rawVariantCode = c?.variant_code ? String(c.variant_code).trim() : '';
      const rawExtId = c?.external_id ? String(c.external_id).trim() : '';
      let resolvedSetCode = c?.set_code || '';
      if (!resolvedSetCode && rawVariantCode && rawVariantCode.includes('-')) {
        resolvedSetCode = rawVariantCode.split('-')[0].trim();
      }
      if (!resolvedSetCode && rawExtId) {
        if (rawExtId.includes('_')) {
          const parts = rawExtId.split('_');
          const last = parts[parts.length - 1].trim();
          if (last.length <= 15) resolvedSetCode = last;
        } else if (rawExtId.includes('-') && !rawExtId.includes(' ')) {
          const first = rawExtId.split('-')[0].trim();
          if (first.length <= 15) resolvedSetCode = first;
        }
      }
      const resolvedCode = c?.code || rawVariantCode || c?.set_code || rawExtId || '';

      return {
        id: c?.id || item.card_id || item.id,
        user_card_id: item.id,
        card_id: c?.id || item.card_id,
        name: c?.name || 'Carta sem nome',
        imageUrl: c?.image_url,
        image_url: c?.image_url,
        external_id: c?.external_id,
        set_code: resolvedSetCode,
        code: resolvedCode,
        variant_code: rawVariantCode || null,
        variant_number: (c?.variant_number && Number(c.variant_number) > 0) ? Number(c.variant_number) : 1,
        game_id: item.game_id || c?.game_id,
        quantity: typeof item.quantity === 'number' ? item.quantity : 1,
        raridade: rarityName
      };
    });
  } catch (err) {
    console.error('Error fetching user cards for collection:', err);
    return [];
  }
};

export const fetchCardsForCollection = async (
  game: string | number,
  setInfo: { id?: number | string; code?: string; name?: string; slug?: string; external_id?: string },
  page: number = 1,
  limit: number = 120
): Promise<{ data: any[]; total: number; totalPages: number }> => {
  try {
    const cardGames = await getCardgames();
    const gameDef = findGame(game);
    const gameData = (cardGames as any[]).find((g: any) => isGame(g.id || g.slug, game)) || gameDef;
    const gameId = gameData?.id || gameDef?.id || getGameId(game, 1);
    let apiSlug = gameDef?.slug || gameData?.slug || getGameSlug(game, 'one-piece');
    if (apiSlug === 'vanguard' || apiSlug.includes('vanguard') || String(game).toLowerCase().includes('vanguard') || String(game) === '11') {
      apiSlug = 'cardfight-vanguard';
    }

    const setCode = (setInfo?.code || '').trim();
    const setName = (setInfo?.name || '').trim();
    const externalId = (setInfo?.external_id || '').trim();

    // Prepare candidate codes for Homura (e.g. OP06, OP-06, SWSH10, SWSH09, SWSH9, etc.)
    const candidateCodes: string[] = [];
    const addCodeVariants = (c: string) => {
      if (!c) return;
      const clean = c.trim();
      if (!clean) return;
      if (!candidateCodes.includes(clean)) candidateCodes.push(clean);
      const withHyphen = clean.replace(/^([A-Za-z]+)(\d+)$/, '$1-$2');
      if (withHyphen !== clean && !candidateCodes.includes(withHyphen)) candidateCodes.push(withHyphen);
      const withoutHyphen = clean.replace(/-/g, '');
      if (withoutHyphen !== clean && !candidateCodes.includes(withoutHyphen)) candidateCodes.push(withoutHyphen);
      // Zero-padding for single digit sets: e.g. SWSH9 -> SWSH09, BW1 -> BW01, XY7 -> XY07
      const withZero = clean.replace(/^([A-Za-z]+)-?(\d)$/, '$1-0$2');
      if (withZero !== clean && !candidateCodes.includes(withZero)) candidateCodes.push(withZero);
      const withZeroNoHyphen = clean.replace(/^([A-Za-z]+)-?(\d)$/, '$10$2');
      if (withZeroNoHyphen !== clean && !candidateCodes.includes(withZeroNoHyphen)) candidateCodes.push(withZeroNoHyphen);
      // Strip leading zeros if present: e.g. SWSH09 -> SWSH9
      const withoutZero = clean.replace(/^([A-Za-z]+)-?0(\d)$/, '$1$2');
      if (withoutZero !== clean && !candidateCodes.includes(withoutZero)) candidateCodes.push(withoutZero);
      // Strip trailing suffixes like -v10, -1st, etc.
      const withoutSuffix = clean.replace(/-v\d+$/i, '').replace(/-1st$/i, '').replace(/-promos$/i, '');
      if (withoutSuffix !== clean && !candidateCodes.includes(withoutSuffix)) candidateCodes.push(withoutSuffix);
    };

    addCodeVariants(setCode);
    addCodeVariants(externalId);
    if (setInfo?.id && !candidateCodes.includes(String(setInfo.id))) {
      addCodeVariants(String(setInfo.id));
    }

    const cleanSetName = (setName || '').replace(/\[.*?\]/g, '').replace(/\(.*?\)/g, '').toLowerCase().trim();
    // Exclude broad era names so we don't accidentally match the entire era's cards
    const eraPrefixes = [
      'sword & shield', 'sword and shield',
      'scarlet & violet', 'scarlet and violet',
      'black & white', 'black and white',
      'sun & moon', 'sun and moon',
      'diamond & pearl', 'diamond and pearl',
      'heartgold & soulsilver', 'heartgold soulsilver',
      'mega evolution', 'xy'
    ];

    const specificSubNames: string[] = [];
    const stripPackagingTerms = (str: string) => {
      return str
        .replace(/booster pack/gi, '')
        .replace(/special booster/gi, '')
        .replace(/extra booster/gi, '')
        .replace(/advanced booster/gi, '')
        .replace(/experience bundle/gi, '')
        .replace(/collector booster/gi, '')
        .replace(/prerelease pack/gi, '')
        .replace(/ver\.\s*\d+(\.\d+)?/gi, '')
        .replace(/ver\s*\d+(\.\d+)?/gi, '')
        .replace(/1st edition/gi, '')
        .replace(/first edition/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const strippedCleanName = stripPackagingTerms(cleanSetName);
    if (strippedCleanName && strippedCleanName.length >= 3 && !eraPrefixes.includes(strippedCleanName) && !specificSubNames.includes(strippedCleanName)) {
      specificSubNames.push(strippedCleanName);
    }

    const nameParts = cleanSetName
      .split(/[—\:\-\/]/)
      .map(s => s.trim().toLowerCase())
      .filter(s => s.length >= 3);

    for (const sn of nameParts) {
      if (!eraPrefixes.includes(sn) && !specificSubNames.includes(sn)) {
        specificSubNames.push(sn);
      }
      const strippedPart = stripPackagingTerms(sn);
      if (strippedPart && strippedPart.length >= 3 && !eraPrefixes.includes(strippedPart) && !specificSubNames.includes(strippedPart)) {
        specificSubNames.push(strippedPart);
      }
    }

    if (cleanSetName && !eraPrefixes.includes(cleanSetName) && !specificSubNames.includes(cleanSetName)) {
      specificSubNames.push(cleanSetName);
    }

    const token = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_HOMURA_TOKEN) || (typeof process !== 'undefined' && process.env ? (process.env.VITE_HOMURA_TOKEN || process.env.HOMURA_TOKEN) : '');
    const apiBaseUrl = getGameApiBaseUrl(gameData?.sauce);

    const fetchHomura = async (params: Record<string, string>, retryCount = 0): Promise<any> => {
      const p = new URLSearchParams({
        page: params.page || page.toString(),
        limit: (limit > 100 ? 100 : limit).toString(),
        ...params
      });
      try {
        const response = await fetch(`${apiBaseUrl}/api/${apiSlug}/cards?${p.toString()}`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        });
        if (response.status === 429 && retryCount < 2) {
          // Rate limited: aguarda e tenta novamente
          await new Promise(r => setTimeout(r, 1200 * (retryCount + 1)));
          return fetchHomura(params, retryCount + 1);
        }
        if (response.ok) {
          const json = await response.json();
          if (json && json.detail && String(json.detail).includes('Muitas') && retryCount < 2) {
            await new Promise(r => setTimeout(r, 1200 * (retryCount + 1)));
            return fetchHomura(params, retryCount + 1);
          }
          return json;
        }
      } catch (e) {
        console.warn('Homura set fetch error:', e);
      }
      return null;
    };

    let result: any = null;
    let successfulParams: Record<string, string> | null = null;

    // Helper para extrair todos os códigos e nomes de sets de uma carta (raiz e todas as variantes)
    const getCardAllSets = (c: any): { codes: string[]; names: string[] } => {
      const codes = new Set<string>();
      const names = new Set<string>();

      const addCode = (val: any) => {
        if (typeof val === 'string' && val.trim()) codes.add(val.toLowerCase().trim());
      };
      const addName = (val: any) => {
        if (typeof val === 'string' && val.trim()) names.add(val.toLowerCase().trim());
      };

      // Raiz
      addCode(c.set_code);
      addCode(c.setCode);
      if (typeof c.set === 'string') {
        if (c.set.length <= 15 && !c.set.includes(' ')) addCode(c.set);
        else addName(c.set);
      } else if (typeof c.set === 'object' && c.set) {
        addCode(c.set.set_code);
        addCode(c.set.code);
        addName(c.set.name);
        addName(c.set.set_name);
        addName(c.set.beauty_name);
      }
      addName(c.set_name);
      addName(c.setName);

      // Todas as variantes
      if (Array.isArray(c.variants)) {
        for (const v of c.variants) {
          if (!v) continue;
          addCode(v.set_code);
          addCode(v.setCode);
          if (v.variant_code && v.variant_code.includes('-')) {
            addCode(v.variant_code.split('-')[0]);
          }
          if (typeof v.set === 'string') {
            if (v.set.length <= 15 && !v.set.includes(' ')) addCode(v.set);
            else addName(v.set);
          } else if (typeof v.set === 'object' && v.set) {
            addCode(v.set.set_code);
            addCode(v.set.code);
            addName(v.set.name);
            addName(v.set.set_name);
            addName(v.set.beauty_name);
          }
          addName(v.set_name);
          addName(v.setName);
        }
      }

      return {
        codes: Array.from(codes),
        names: Array.from(names)
      };
    };

    // Helper to verify if an API response really belongs to the requested set and is not an unfiltered whole-DB dump
    const isMatchingSetResult = (res: any): boolean => {
      if (!res || !Array.isArray(res.data) || res.data.length === 0) return false;

      // Reject responses that returned an unfiltered whole-DB dump (e.g. Pokemon total > 4000 when an invalid param is passed)
      if (typeof res.total === 'number' && res.total > 4000) {
        return false;
      }

      const lowerCodes = candidateCodes.map(c => c.toLowerCase().trim()).filter(Boolean);

      return res.data.some((c: any) => {
        const { codes: cardCodes, names: cardNames } = getCardAllSets(c);

        for (const lc of lowerCodes) {
          for (const cc of cardCodes) {
            if (
              cc === lc ||
              cc.startsWith(lc + ':') ||
              cc.startsWith(lc + '-') ||
              cc.startsWith(lc + '_') ||
              cc.replace(/[-_]/g, '') === lc.replace(/[-_]/g, '')
            ) {
              return true;
            }
          }
          for (const cn of cardNames) {
            if (
              cn.startsWith(lc + ':') ||
              cn.startsWith(lc + ' ') ||
              cn.includes(`[${lc}]`) ||
              cn.includes(`(${lc})`)
            ) {
              return true;
            }
          }
        }

        for (const sn of specificSubNames) {
          for (const cn of cardNames) {
            if (cn && (cn.includes(sn) || sn.includes(cn))) return true;
          }
          for (const cc of cardCodes) {
            if (cc && (cc.includes(sn) || sn.includes(cc))) return true;
          }
        }

        if (cleanSetName) {
          for (const cn of cardNames) {
            if (cn === cleanSetName || cleanSetName.includes(cn) || cn.includes(cleanSetName)) {
              return true;
            }
          }
        }

        return false;
      });
    };

    // 1. Try candidate set codes with parameter 'set' (now accepts code or name across all games)
    for (const code of candidateCodes) {
      const queryParams = { set: code };
      const res = await fetchHomura(queryParams);
      if (isMatchingSetResult(res)) {
        result = res;
        successfulParams = queryParams;
        break;
      }
    }

    // 2. Try set name with parameter 'set' (e.g. 'Chilling Reign', 'SWSH06: Chilling Reign', 'Supreme Darkness')
    if (!result && (setName || specificSubNames.length > 0)) {
      const namesToTry = [
        setName,
        cleanSetName,
        strippedCleanName,
        ...specificSubNames
      ].filter(Boolean);

      const uniqueNames = Array.from(new Set(namesToTry));
      for (const n of uniqueNames) {
        const queryParams = { set: n };
        const res = await fetchHomura(queryParams);
        if (isMatchingSetResult(res)) {
          result = res;
          successfulParams = queryParams;
          break;
        }
      }
    }

    // 3. Fallback: try legacy parameters (set_code, set_name, id, code) if still not resolved
    if (!result) {
      for (const code of candidateCodes) {
        const queryParams = { set_code: code };
        const res = await fetchHomura(queryParams);
        if (isMatchingSetResult(res)) {
          result = res;
          successfulParams = queryParams;
          break;
        }
      }
    }

    if (!result && specificSubNames.length > 0) {
      for (const n of specificSubNames) {
        const queryParams = { set_name: n };
        const res = await fetchHomura(queryParams);
        if (isMatchingSetResult(res)) {
          result = res;
          successfulParams = queryParams;
          break;
        }
      }
    }

    if (!result && setCode) {
      let queryParams: Record<string, string> = { id: setCode };
      let res = await fetchHomura(queryParams);
      if (isMatchingSetResult(res)) {
        result = res;
        successfulParams = queryParams;
      } else {
        queryParams = { code: setCode };
        res = await fetchHomura(queryParams);
        if (isMatchingSetResult(res)) {
          result = res;
          successfulParams = queryParams;
        }
      }
    }

    let list = Array.isArray(result) ? result : (result?.data || []);

    // Multi-page fetch: If total is greater than first page (up to 8 pages / 800 cards)
    if (result && result.total && result.total > list.length && successfulParams && list.length > 0) {
      const maxPages = Math.min(8, Math.ceil(result.total / 100));
      for (let p = 2; p <= maxPages; p++) {
        // Pausa pequena de 100ms para evitar rate limiting
        await new Promise(r => setTimeout(r, 100));
        const extraRes = await fetchHomura({ ...successfulParams, page: p.toString() });
        if (extraRes && Array.isArray(extraRes.data)) {
          list = list.concat(extraRes.data);
        }
      }
    }

    // Filter cards to ensure only cards belonging to the set are included
    if (list.length > 0 && (candidateCodes.length > 0 || specificSubNames.length > 0)) {
      const lowerCodes = candidateCodes.map(c => c.toLowerCase().trim()).filter(Boolean);
      const isCardFromSet = (c: any): boolean => {
        const { codes: cardCodes, names: cardNames } = getCardAllSets(c);

        if (cardCodes.length === 0 && cardNames.length === 0) return true;

        for (const lc of lowerCodes) {
          for (const cc of cardCodes) {
            if (
              cc === lc || 
              cc.startsWith(lc + ':') || 
              cc.startsWith(lc + '-') || 
              cc.startsWith(lc + '_') || 
              cc.replace(/[-_]/g, '') === lc.replace(/[-_]/g, '')
            ) {
              return true;
            }
          }
          for (const cn of cardNames) {
            if (
              cn.startsWith(lc + ':') ||
              cn.startsWith(lc + ' ') ||
              cn.includes(`[${lc}]`) ||
              cn.includes(`(${lc})`)
            ) {
              return true;
            }
          }
        }

        for (const sn of specificSubNames) {
          for (const cn of cardNames) {
            if (cn && (cn.includes(sn) || sn.includes(cn))) return true;
          }
          for (const cc of cardCodes) {
            if (cc && (cc.includes(sn) || sn.includes(cc))) return true;
          }
        }

        if (cleanSetName) {
          for (const cn of cardNames) {
            if (cn === cleanSetName || cleanSetName.includes(cn) || cn.includes(cleanSetName)) {
              return true;
            }
          }
        }

        return false;
      };

      const filtered = list.filter(isCardFromSet);
      if (filtered.length > 0) {
        list = filtered;
      }
    }

    // Deduplicate cards by ID / external_id / code
    const seenIds = new Set<string>();
    list = list.filter((c: any) => {
      const uid = String(c.id || c.external_id || `${c.code}_${c.name}`);
      if (seenIds.has(uid)) return false;
      seenIds.add(uid);
      return true;
    });

    let externalData: any[] = [];
    if (list.length > 0) {
      // Processamento centralizado com parseHomuraCard
      externalData = list.map((c: any) => parseHomuraCard(c, {
        game: apiSlug,
        gameName: gameData?.name || String(game),
        gameId: gameId,
        cardBackUrl: gameData?.card_back_url,
        fallbackSetCode: setCode,
        fallbackSetName: setName
      }));

      const total = externalData.length;
      
      // Alimenta o cache com os preços obtidos externamente
      externalData.forEach((cd: any) => {
        if (cd.price > 0 && (cd.code || cd.id || cd.external_id)) {
          setPriceInCache(resolveGameSlug(game), cd.price, undefined, cd.code || cd.external_id || cd.id);
        }
      });

      return {
        data: externalData,
        total: total,
        totalPages: Math.ceil(total / limit)
      };
    }

    return { data: [], total: 0, totalPages: 0 };
  } catch (err) {
    console.error('Error in fetchCardsForCollection:', err);
    return { data: [], total: 0, totalPages: 0 };
  }
};

